/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { MenuProps, notification } from 'antd';

import { IComponentProps, ILanguage } from 'interfaces/queries/language';
import usePreferredLang from './preferredLang';

import 'dayjs/locale/fr';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import dayjs from 'dayjs';
import {
  fetchTranslation,
  requestSupportedLanguage,
  setDefaultLanguage,
} from 'queries/languages/lang';
import React from 'react';
import { capitalize } from 'utils/capitalizeWords';
import { requestChangeLanguage } from 'queries/auth/user';
import Toast from 'components/shared/Alert';
import { pharmacyFallbacks } from 'utils/pharmacyDictionary';
import { jewelryFallbacks } from 'utils/jewelryDictionary';
import { storefrontFallbacks } from 'utils/storefrontDictionary';
import { mobileMoneyFallbacks } from 'utils/mobileMoneyDictionary';
import { hotelFallbacks } from 'utils/hotelDictionary';
import { billingFallbacks } from 'utils/billingDictionary';
import { verticalsFallbacks } from 'utils/verticalsDictionary';

dayjs.extend(localizedFormat);
dayjs.locale('en');

const refreshToken = localStorage.getItem('refreshToken');

interface LanguageContextProps {
  translate: (_text?: string, _index?: number | string) => string;
  menuProps: MenuProps;
  currentLang: ICurrentLang;
  isLoading: boolean;
  isLoadingLanguage: boolean;
  currentLanguage: string | null;
  formatDate: (_date: string) => any;
  loadChangeLang: boolean;
}

interface ICurrentLang {
  key: number | string;
  label: string;
  icon: React.ReactNode;
}

interface LanguagePayload {
  lang: string;
  device_id: string;
}

const generateDeviceId = (): string => {
  let deviceId = localStorage.getItem('deviceId');

  if (!deviceId) {
    deviceId = 'dev_' + crypto.randomUUID();
    localStorage.setItem('deviceId', deviceId);
  }

  return deviceId;
};

const defaultLang = 'en';
const LanguageContext = createContext<LanguageContextProps | null>(null);

const LanguageProvider: React.FC<IComponentProps> = ({ children }) => {
  const [deviceId, setDeviceId] = useState<string>('');
  const [api, contectHolder] = notification.useNotification();
  const {
    currentLanguage,
    savePreferredLang,
    currentLanguageData,
    savePreferredLangData,
  } = usePreferredLang();

  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    currentLanguage || defaultLang
  );

  const { languages, isLoading } = requestSupportedLanguage();

  const {
    isSuccess: isSetLangSuccess,
    isLoadingLanguage: loadSetLang,
    submitData: requestPayload,
  } = setDefaultLanguage();

  const {
    loadChangeLang,
    changeLang,
    changeLangResponse,
    successChangeLang,
    errorChangeLang,
    clearChangeLangResponse,
  } = requestChangeLanguage();

  const { isSuccess, languageData, isLoadingLanguage } = fetchTranslation();

  const [isManualChange, setIsManualChange] = useState(false);

  const handleMenuClick: MenuProps['onClick'] = (value) => {
    setIsManualChange(true);
    const targetLang = String(value.key).toLowerCase().includes('fr') || value.key === '2' ? 'fr' : 'en';
    savePreferredLang(targetLang);
    setSelectedLanguage(targetLang);
    localStorage.setItem('currentLanguage', targetLang);
    localStorage.setItem('preferredLang', targetLang);
    localStorage.setItem('lang', targetLang);
    localStorage.setItem('i18nextLng', targetLang);
  };

  // Build items with deduplication (by lowercase key to handle "en" vs "En" vs "English")
  const items: MenuProps['items'] | undefined = useMemo(() => {
    if (!languages) return [];
    
    const seen = new Set<string>();
    const result: Array<{ key: string; label: string; value: string }> = [];
    
    for (const item of languages as ILanguage[]) {
      let key = item.language || '';
      let normalizedKey = key.toLowerCase();
      
      // Standardize English and French keys/labels to avoid duplicates
      if (normalizedKey === 'english' || normalizedKey === 'en') {
        normalizedKey = 'en';
        key = 'en';
      } else if (normalizedKey === 'french' || normalizedKey === 'fr') {
        normalizedKey = 'fr';
        key = 'fr';
      }
      
      if (normalizedKey && !seen.has(normalizedKey)) {
        seen.add(normalizedKey);
        result.push({
          key: key,
          label: key === 'en' ? 'En' : key === 'fr' ? 'Fr' : capitalize(key),
          value: item.id,
        });
      }
    }
    return result as MenuProps['items'];
  }, [languages]);

  const currentLang: any = useMemo(() => {
    if (!items) return null;
    return items.find((lang: any) => {
      let k = lang?.key?.toLowerCase();
      let s = selectedLanguage?.toLowerCase();
      if (k === 'en' || k === 'english') k = 'en';
      if (s === 'en' || s === 'english') s = 'en';
      if (k === 'fr' || k === 'french') k = 'fr';
      if (s === 'fr' || s === 'french') s = 'fr';
      return k === s;
    });
  }, [items, selectedLanguage]);

  useEffect(() => {
    const id = generateDeviceId();
    setDeviceId(id);
  }, []);

  useEffect(() => {
    if (!refreshToken && selectedLanguage && selectedLanguage != null) {
      requestPayload({ lang: selectedLanguage, device_id: deviceId });
    }
  }, [selectedLanguage]);

  useEffect(() => {
    if (refreshToken && selectedLanguage && selectedLanguage != null) {
      requestPayload({ lang: selectedLanguage, device_id: deviceId });
      const langId = Number(currentLang?.value);
      if (langId && !isNaN(langId)) {
        changeLang({ language_id: langId });
      }
    }
  }, [selectedLanguage, currentLang]);

  useEffect(() => {
    const checkDefaultLang = defaultLang != currentLanguage;
    if (checkDefaultLang && isSuccess) {
      const langData = JSON.stringify(languageData);
      savePreferredLangData(langData);
    }

    if (currentLanguage) {
      setSelectedLanguage(currentLanguage);
    }
  }, [currentLang, currentLanguage, languageData]);

  const menuProps = {
    items,
    onClick: handleMenuClick,
  };

  const translationMap = useMemo(() => {
    if (!currentLanguageData || !Array.isArray(currentLanguageData)) return new Map();
    const map = new Map();
    for (const item of currentLanguageData) {
      const typedItem = item as any;
      if (typedItem[defaultLang]) {
        map.set(typedItem[defaultLang].toLowerCase(), typedItem);
      }
    }
    return map;
  }, [currentLanguageData]);

  const translate = React.useCallback(
    (text: string | null | undefined = ''): string => {
      // Safety check for null/undefined to prevent runtime errors like toLowerCase() on null
      if (!text || typeof text !== 'string') {
        return text || '';
      }

      // Dynamic clinical safety alert translation logic
      if (text.startsWith("Safety Alert: Patient is allergic to")) {
        if (currentLanguage === 'fr') {
          const allergyMatch = text.match(/allergic to\s+(.*?)\s+\(matches/);
          const medMatch = text.match(/medication:\s+'(.*?)'/);
          const allergy = allergyMatch ? allergyMatch[1] : '';
          const med = medMatch ? medMatch[1] : '';
          return `Alerte de Sécurité : Le patient est allergique à ${allergy} (correspond au médicament prescrit : '${med}')`;
        }
      }
      if (text.startsWith("Safety Alert: Medication")) {
        if (currentLanguage === 'fr') {
          const medMatch = text.match(/Medication\s+'(.*?)'\s+is/);
          const condMatch = text.match(/condition:\s+'(.*?)'/);
          const med = medMatch ? medMatch[1] : '';
          const cond = condMatch ? condMatch[1] : '';
          return `Alerte de Sécurité : Le médicament '${med}' est contre-indiqué pour la maladie chronique du patient : '${cond}'`;
        }
      }

      const activeLang = (typeof window !== 'undefined' && (localStorage.getItem('currentLanguage') || localStorage.getItem('preferredLang'))) || currentLanguage || 'en';
      const isFrenchActive = String(activeLang).toLowerCase().includes('fr') || activeLang === '2';

      if (isFrenchActive) {
        if (text.match(/^\+?\d+\s+spools?$/i)) {
          const count = text.replace(/spools?/i, '').trim();
          return `${count} touret${count.includes('1') && !count.includes('11') ? '' : 's'}`;
        }
        if (text.match(/^\d+\s+due today$/i)) {
          const count = text.split(/\s+/)[0];
          return `${count} dus aujourd'hui`;
        }
        if (text.startsWith("Paid:")) {
          return text.replace("Paid:", "Payé :").replace("(cash)", "(espèces)").replace("(momo)", "(MoMo)").replace("(card)", "(carte)");
        }
        if (text.startsWith("Scrap:")) {
          return text.replace("Scrap:", "Chute :").replace("meters", "mètres").replace("meter", "mètre");
        }
        if (text.startsWith("Operator #")) {
          return text.replace("Operator #", "Opérateur n°");
        }
        if (text.startsWith("Cut Stock Logged")) {
          return text.replace("Cut Stock Logged", "Coupe de Touret Enregistrée").replace("meters", "mètres").replace("meter", "mètre");
        }
        if (text.startsWith("POS Credit Sale")) {
          return text.replace("POS Credit Sale", "Vente à Crédit POS");
        }
        if (text.startsWith("POS Sale")) {
          return text.replace("POS Sale", "Vente POS");
        }
      }

      // Local fallbacks for common strings and new menu items
      const fallbacks: Record<string, Record<string, string>> = {
        fr: {
          'Customer Support': 'Support Client',
          "Hi! Have a question or facing an issue? Send us a message directly and we'll help you get it resolved.": "Bonjour ! Vous avez une question ou rencontrez un problème ? Envoyez-nous un message directement et nous vous aiderons à le résoudre.",
          'Topic': 'Sujet',
          'Choose issue topic': 'Choisissez le sujet',
          'Describe your question or issue...': 'Décrivez votre question ou problème...',
          'Send Message': 'Envoyer le message',
          'Please select a topic': 'Veuillez sélectionner un sujet',
          'Please enter your message': 'Veuillez saisir votre message',
          'Message Sent': 'Message envoyé',
          'Your support request has been submitted successfully. Our team will get back to you shortly.': 'Votre demande de support a été soumise avec succès. Notre équipe vous répondra sous peu.',
          'Close Window': 'Fermer la fenêtre',
          'Waiter Tip': 'Pourboire Serveur',
          'Load More': 'Charger plus',
          'Sync Data': 'Synchroniser les données',
          'Data synchronized successfully': 'Données synchronisées avec succès',
          'Sync failed': 'Échec de la synchronisation',
          'Menu Management': 'Gestion des Menus',
          'Create New Menu': 'Créer un Nouveau Menu',
          'Search menus...': 'Rechercher menus...',
          'Total Menus': 'Total Menus',
          'Happy Hour': 'Happy Hour',
          'Seasonal': 'Saisonnier',
          'No description': 'Pas de description',
          'Create Your First Menu': 'Créer votre premier menu',
          'Manage': 'Gérer',
          'Your subscription is expiring soon! Please renew your workspace license to ensure uninterrupted service.': 'Votre abonnement expire bientôt ! Veuillez renouveler votre licence d\'espace de travail pour garantir un service ininterrompu.',
          'Your subscription has expired! You are in a grace period. Please renew to avoid service suspension.': 'Votre abonnement a expiré ! Vous êtes dans une période de grâce. Veuillez le renouveler pour éviter la suspension du service.',
          'Renew Now': 'Renouveler maintenant',
          'Available Tables': 'Tables Disponibles',
          'Today Reservations': 'Réservations du Jour',
          'Avg Prep Time': 'Temps Moyen de Préparation',
          'Table View': 'Vue des Tables',
          'Reservations': 'Réservations',
          'Kitchen Display': 'Écran de Cuisine',
          'New Order': 'Nouvelle Commande',
          'Active Orders': 'Commandes Actives',
          'Floor Plan': 'Plan de Salle',
          'Search customers': 'Rechercher des clients',
          'Search menu items...': 'Rechercher des articles...',
          'Food Menu': 'Menu Plats',
          'View Order': 'Voir la commande',
          'Empty Tab': 'Onglet Vide',
          'Select items from the menu to build the order': 'Sélectionnez des articles du menu pour composer la commande',
          'Coffee Shop Management': 'Gestion du Café / Coffee Shop',
          'COFFEE SHOP': 'CAFÉ / COFFEE SHOP',
          'Table Mode': 'Mode Table',
          'Counter Mode': 'Vente au Comptoir',
          'Fukkoni Portal': 'Portail Fukkoni',
          'Sign Out': 'Se déconnecter',
          'Verification Error': 'Erreur de vérification',
          'Super Admin Portal': 'Portail Super Admin',
          'Enterprise Management Dashboard': 'Tableau de bord de gestion d\'entreprise',
          'Logout': 'Se déconnecter',
          'Total Shops': 'Nombre total de boutiques',
          'active businesses': 'entreprises actives',
          'Total Users': 'Nombre total d\'utilisateurs',
          'Platform-wide accounts': 'Comptes sur l\'ensemble de la plateforme',
          'Active SaaS Subs': 'Abonnements SaaS actifs',
          'Recurring revenue stream': 'Flux de revenus récurrents',
          'Website Visits': 'Visites du site web',
          'Last 30 days traffic': 'Trafic des 30 derniers jours',
          'TOTAL PLATFORM INCOME (MRR)': 'REVENU TOTAL DE LA PLATEFORME (MRR)',
          'Annual Run Rate (ARR)': 'Taux de rendement annuel (ARR)',
          'Total Sales Processed': 'Ventes totales traitées',
          'Active Subscriptions': 'Abonnements actifs',
          'Businesses': 'Entreprises',
          'seats': 'sièges',
          'seats used': 'sièges utilisés',
          'User Seat Allocation': 'Allocation des sièges utilisateurs',
          'User limit reached. Please upgrade your subscription to add more users.': 'Limite d\'utilisateurs atteinte. Veuillez mettre à niveau votre abonnement pour ajouter d\'autres utilisateurs.',
          'Income Distribution by Service Tier': 'Distribution des revenus par niveau de service',
          'Proportional income contributed by specialized systems': 'Revenu proportionnel apporté par les systèmes spécialisés',
          'LIVE AUDIT': 'AUDIT EN DIRECT',
          'Frequently accessed management tools': 'Outils de gestion fréquemment consultés',
          'Manage Plans': 'Gérer les forfaits',
          'PLAN ARCHITECTURE': 'ARCHITECTURE DES PLANS',
          'BEST VALUE': 'MEILLEUR CHOIX',
          'BUSINESS CATEGORY': 'CATÉGORIE D\'ENTREPRISE',
          'USER SEAT LIMIT': 'LIMITE DE SIÈGES UTILISATEURS',
          'Hidden': 'Masqué',
          'MONTHLY RATE': 'TARIF MENSUEL',
          'INCLUDED FEATURES': 'FONCTIONNALITÉS INCLUSES',
          'additional capabilities': 'capacités supplémentaires',
          'Edit Architecture': 'Modifier l\'architecture',
          'Decommission Plan?': 'Déclasser le plan ?',
          'All shops on this plan will remain active but new signups will be blocked.': 'Toutes les boutiques associées à ce plan resteront actives, mais les nouvelles inscriptions seront bloquées.',
          'Yes, Decommission': 'Oui, déclasser',
          'Subscription Models': 'Modèles d\'abonnement',
          'Define platform service tiers and commercial boundaries': 'Définir les niveaux de service de la plateforme et les limites commerciales',
          'Config': 'Configuration',
          'New Service Tier': 'Nouveau niveau de service',
          'ALL TIER MODELS': 'TOUS LES MODÈLES DE NIVEAU',
          'HOSPITAL PLATFORMS': 'PLATEFORMES D\'HÔPITAUX',
          'PHARMACY SYSTEMS': 'SYSTÈMES DE PHARMACIE',
          'RESTAURANT SYSTEMS': 'SYSTÈMES DE RESTAURANT',
          'RETAIL / SHOPS': 'COMMERCE / BOUTIQUES',
          'No subscription tiers defined for this service category.': 'Aucun niveau d\'abonnement défini pour cette catégorie de service.',
          'Create First Plan': 'Créer le premier plan',
          'Refine Service Tier': 'Ajuster le niveau de service',
          'Design New Tier': 'Concevoir un nouveau niveau',
          'TIER NAME': 'NOM DU NIVEAU',
          'Identity required': 'Identité requise',
          'Valuation required': 'Évaluation requise',
          'MARKET HIGHLIGHT': 'MISE EN VALEUR DU MARCHÉ',
          'Mark as Recommended': 'Marquer comme recommandé',
          'MAX USER SEATS': 'SIÈGES UTILISATEURS MAX',
          'Please define the user limit': 'Veuillez définir la limite d\'utilisateurs',
          'VISIBILITY': 'VISIBILITÉ',
          'Show on Website': 'Afficher sur le site web',
          'SERVICE CATEGORY': 'CATÉGORIE DE SERVICE',
          'Shop / Retail System': 'Système de boutique / détail',
          'Hospital Platform': 'Plateforme d\'hôpital',
          'Pharmacy System': 'Système de pharmacie',
          'Restaurant System': 'Système de restaurant',
          'CAPABILITIES (ONE PER LINE)': 'CAPACITÉS (UNE PAR LIGNE)',
          'Manifest required': 'Descriptif requis',
          'Discard': 'Abandonner',
          'Update Policy': 'Mettre à jour la politique',
          'PREMIUM FEATURE LOCKED': 'FONCTIONNALITÉ PREMIUM VERROUILLÉE',
          'This module is not included in your current subscription plan. Please upgrade your pricing plan to unlock this capability.': 'Ce module n\'est pas inclus dans votre plan d\'abonnement actuel. Veuillez mettre à niveau votre forfait pour débloquer cette fonctionnalité.',
          'Upgrade Plan': 'Mettre à jour le forfait',
          'SUBSCRIPTION PLAN TIER': "NIVEAU DE PLAN D'ABONNEMENT",
          'Please select subscription plan tier': "Veuillez sélectionner le niveau de plan d'abonnement",
          'Starter Plan (Standard Features)': 'Forfait Starter (Fonctionnalités standards)',
          'Professional Plan (Premium Features)': 'Forfait Professionnel (Fonctionnalités premium)',
          'Enterprise Plan (Maximum Scale)': 'Forfait Enterprise (Échelle maximale)',
          'Enterprise Plan': 'Forfait Enterprise',
          'Starter Plan': 'Forfait Starter',
          'Professional Plan': 'Forfait Professionnel',
          'Provision Tier': 'Fournir le niveau',
          'Shop / Retail': 'Boutique / Détail',
          'Hospital': 'Hôpital',
          'Restaurant': 'Restaurant',
          'PLAN NAME': 'NOM DU FORFAIT',
          'MONTHLY PRICE': 'PRIX MENSUEL',
          'POPULAR': 'POPULAIRE',
          'Live system events': 'Événements système en direct',
          'Last 7 days': '7 derniers jours',
          'Last 30 days': '30 derniers jours',
          'No recent activity detected': 'Aucune activité récente détectée',
          'System Health': 'Santé du système',
          'All systems operational. No reported issues in the last 24 hours.': 'Tous les systèmes sont opérationnels. Aucun problème signalé au cours des dernières 24 heures.',
          'Run System Check': 'Exécuter la vérification du système',
          'Developer Audit & Live HTTP Monitor': 'Audit développeur & Moniteur HTTP en direct',
          'Close Monitor': 'Fermer le moniteur',
          'Clear Logs': 'Effacer les journaux',
          'Cleared logs': 'Journaux effacés',
          'Pinging API and Database server health...': 'Ping de la santé du serveur API et de la base de données...',
          'Backend Connection Successful': 'Connexion backend réussie',
          'The database, cache layers, and routing endpoints are fully operational.': 'La base de données, les couches de cache et les points de terminaison de routage sont entièrement opérationnels.',
          'System Warning / Degradation detected': 'Avertissement système / Dégradation détectée',
          'Live Traffic Logs': 'Journaux de trafic en direct',
          'Axios Agent Active': 'Agent Axios actif',
          'Filter by endpoint path...': 'Filtrer par chemin de point de terminaison...',
          'Errors Only': 'Erreurs uniquement',
          'Success Only': 'Succès uniquement',
          'No HTTP traffic recorded yet. Trigger dashboard actions to track live logs.': 'Aucun trafic HTTP enregistré pour le moment. Déclenchez des actions sur le tableau de bord pour suivre les journaux en direct.',
          'Request Details': 'Détails de la demande',
          'Close Pane': 'Fermer le volet',
          'REQUEST HEADERS': 'EN-TÊTES DE REQUÊTE',
          'QUERY PARAMS': 'PARAMÈTRES DE REQUÊTE',
          'REQUEST PAYLOAD': 'CHARGE UTILE DE LA REQUÊTE',
          'RESPONSE BODY': 'CORPS DE LA RÉPONSE',
          'EVENT ACTION': "ACTION D'ÉVÉNEMENT",
          'ACTOR': 'ACTEUR',
          'SYSTEM PROCESS': 'PROCESSUS SYSTÈME',
          'SCOPE': 'PORTÉE',
          'GLOBAL': 'GLOBAL',
          'DESCRIPTION': 'DESCRIPTION',
          'IP ADDRESS': 'ADRESSE IP',
          'TIMESTAMP': 'HORODATAGE',
          'RESULT': 'RÉSULTAT',
          'Audit Trails': "Pistes d'Audit",
          'Real-time oversight of critical platform events': 'Surveillance en temps réel des événements critiques de la plateforme',
          'Search by action, user, or IP...': 'Rechercher par action, utilisateur ou IP...',
          'Filter': 'Filtrer',
          'entries': 'entrées',
          'SUCCESS': 'SUCCÈS',
          'Welcome, Super Admin!': 'Bienvenue, Super Admin !',
          'Access denied. Super Admin credentials required.': "Accès refusé. Informations d'identification de super administrateur requises.",
          'Invalid credentials. Please try again.': 'Identifiants invalides. Veuillez réessayer.',
          'Super Admin': 'Super Administrateur',
          'Platform Management Portal': 'Portail de gestion de la plateforme',
          'Please enter your email': 'Veuillez entrer votre adresse e-mail',
          'Please enter a valid email': 'Veuillez entrer une adresse e-mail valide',
          'Admin Email': "E-mail de l'administrateur",
          'Please enter your password': 'Veuillez entrer votre mot de passe',
          'Access Portal': 'Accéder au portail',
          'Authorized personnel only. All actions are logged.': 'Personnel autorisé uniquement. Toutes les actions sont enregistrées.',
          'Transaction reference copied!': 'Référence de transaction copiée !',
          'CLIENT / BUSINESS': 'CLIENT / ENTREPRISE',
          'PRODUCT DESCRIPTION': 'DESCRIPTION DU PRODUIT',
          'Subscription renewal': "Renouvellement d'abonnement",
          'TX REFERENCE': 'RÉFÉRENCE DE TRANSACTION',
          'AMOUNT': 'MONTANT',
          'CASH PAYMENT': 'PAIEMENT EN ESPÈCES',
          'ONLINE (CARD/MOMO)': 'EN LIGNE (CARTE/MOMO)',
          'STATUS': 'STATUT',
          'DATE & TIME': 'DATE & HEURE',
          'Payments & Renewals': 'Paiements & Renouvellements',
          'Audit and monitor all platform checkout invoices and manually authorized renewals': 'Auditer et surveiller toutes les factures de paiement de la plateforme et les renouvellements autorisés manuellement',
          'TOTAL REVENUE': 'REVENU TOTAL',
          'Aggregated successful invoices': 'Factures réussies agrégées',
          'SUCCESSFUL PAYMENTS': 'PAIEMENTS RÉUSSIS',
          'Completed invoice records': 'Enregistrements de factures terminés',
          'CASH TRANSACTIONS': 'TRANSACTIONS EN ESPÈCES',
          'Manually credited renew requests': 'Demandes de renouvellement créditées manuellement',
          'ONLINE CHECKOUTS': 'PAIEMENTS EN LIGNE',
          'Initiated Flutterwave sessions': 'Sessions Flutterwave initiées',
          'Search by client, reference, email...': 'Rechercher par client, référence, e-mail...',
          'Reset Filters': 'Réinitialiser les filtres',
          'platform payments': 'paiements de la plateforme',
          'System Configurations': 'Configurations du système',
          'Manage localization settings including supported languages, platform currencies, and country-specific checkout payment methods': 'Gérer les paramètres de localisation, y compris les langues prises en charge, les devises de la plateforme et les modes de paiement de caisse spécifiques au pays',
          'Languages': 'Langues',
          'Configure supported application translation languages': "Configure les langues de traduction de l'application prises en charge",
          'Add Language': 'Ajouter une langue',
          'Currencies': 'Devises',
          'Search currencies by code, country, or description...': 'Rechercher des devises par code, pays ou description...',
          'Add Currency': 'Ajouter une devise',
          'Countries & Payments': 'Pays & Paiements',
          'Search countries by name or code...': 'Rechercher des pays par nom ou code...',
          'Add Country': 'Ajouter un pays',
          'Edit Supported Language': 'Modifier la langue prise en charge',
          'Add Supported Language': 'Ajouter une langue prise en charge',
          'Language Name / Code': 'Nom / Code de la langue',
          'Please enter language name (e.g. Swahili, French)': 'Veuillez entrer le nom de la langue (ex. Swahili, Français)',
          'Register Language': 'Enregistrer la langue',
          'Register Platform Currency': 'Enregistrer la devise de la plateforme',
          'Currency Code (ISO)': 'Code de devise (ISO)',
          'Please enter ISO currency code (e.g. USD, RWF)': 'Veuillez entrer le code de devise ISO (ex. USD, RWF)',
          'Country Name': 'Nom du pays',
          'Please enter country name': 'Veuillez entrer le nom du pays',
          'Currency Name/Description': 'Nom/Description de la devise',
          'Please enter currency description': 'Veuillez entrer la description de la devise',
          'Add New Country Support': 'Ajouter un nouveau pays pris en charge',
          'Country Code (ISO 2-letter)': 'Code pays (ISO 2 lettres)',
          'Please enter ISO 2-letter code': 'Veuillez entrer le code ISO 2 lettres',
          'Phone Dialing Code': 'Indicatif téléphonique',
          'Please enter phone dialing code': "Veuillez entrer l'indicatif téléphonique",
          'Country Emoji Flag': 'Drapeau émoji du pays',
          'Please enter country flag emoji': "Veuillez entrer l'émoji du drapeau du pays",
          'Configure Payments': 'Configurer les paiements',
          'FLUTTERWAVE GATEWAY (ONLINE CARD/MOMO)': 'PASSERELLE FLUTTERWAVE (CARTE EN LIGNE/MOMO)',
          'ENABLED': 'ACTIVÉ',
          'DISABLED': 'DÉSACTIVÉ',
          'CASH PAYMENT METHOD': 'MODE DE PAIEMENT EN ESPÈCES',
          'CASH INSTRUCTIONS': "INSTRUCTIONS POUR L'ESPÈCE",
          'Please provide cash renewal guidelines': 'Veuillez fournir des directives de renouvellement en espèces',
          'Save Configurations': 'Sauvegarder les configurations',
          'LANGUAGE NAME / CODE': 'NOM / CODE DE LA LANGUE',
          'ACTIONS': 'ACTIONS',
          'Edit': 'Modifier',
          'CURRENCY CODE': 'CODE DE DEVISE',
          'COUNTRY': 'PAYS',
          'FLAG': 'DRAPEAU',
          'NAME': 'NOM',
          'ISO CODE': 'CODE ISO',
          'DIALING CODE': 'INDICATIF',
          'PAYMENT CHANNELS': 'CANAUX DE PAIEMENT',
          'Online': 'En ligne',
          'Cash': 'Espèces',
          'None': 'Aucun',
          'Shop Management': 'Gestion des Boutiques',
          'Monitor and provision business environments': "Surveiller et provisionner les environnements d'entreprise",
          'Filter by name, owner, or UID...': 'Filtrer par nom, propriétaire ou UID...',
          'Create Shop': 'Créer une Boutique',
          'shops': 'boutiques',
          'Shop Details': 'Détails de la Boutique',
          'Business Information': 'Informations sur l\'entreprise',
          'Industry': 'Secteur d\'activité',
          'Onboarded': 'Inscrit le',
          'Owner Contact': 'Contact du Propriétaire',
          'Phone': 'Téléphone',
          'Access Status': 'Statut d\'Accès',
          'Expiration Date': 'Date d\'Expiration',
          'Service Code': 'Code de Service',
          'Create New Business Environment': 'Créer un nouvel environnement d\'entreprise',
          'Business name is required': 'Le nom de l\'entreprise est requis',
          'Owner Username': 'Nom d\'utilisateur du propriétaire',
          'Owner username is required': 'Le nom d\'utilisateur du propriétaire est requis',
          'Owner Email Address': 'Adresse e-mail du propriétaire',
          'Owner email is required': 'L\'e-mail du propriétaire est requis',
          'Please enter a valid email address': 'Veuillez entrer une adresse e-mail valide',
          'Owner Password': 'Mot de passe du propriétaire',
          'Password must be at least 8 characters': 'Le mot de passe doit contenir au moins 8 caractères',
          'Owner Phone Number': 'Numéro de téléphone du propriétaire',
          'Phone number is required': 'Le numéro de téléphone est requis',
          'Industry Segment': 'Segment d\'activité',
          'Please select industry segment': 'Veuillez sélectionner un secteur d\'activité',
          'Select Industry': 'Sélectionner le secteur',
          'Initial Subscription Period': 'Période d\'abonnement initiale',
          'Select period': 'Sélectionner la période',
          '1 Month (Standard trial)': '1 mois (essai standard)',
          '12 Months (Annual plan)': '12 mois (plan annuel)',
          'Province/State': 'Province/État',
          'Street Address': 'Adresse de rue',
          'Test Shop': 'Boutique de Test',
          'Premium Tier': 'Niveau Premium',
          'Create & Onboard Shop': 'Créer & Intégrer la Boutique',
          'Manual Platform Renewal': 'Renouvellement manuel de la plateforme',
          'Directly extend the active status of this shop in the database. Use this when manual cash payments are collected.': 'Prolongez directement le statut actif de cette boutique dans la base de données. Utilisez cette option lorsque des paiements manuels en espèces sont perçus.',
          'EXTENSION PERIOD': 'PÉRIODE D\'EXTENSION',
          '1 Month (Standard Renewal)': '1 mois (renouvellement standard)',
          '3 Months Extension': 'Extension de 3 mois',
          '6 Months Extension': 'Extension de 6 mois',
          '12 Months (Annual Subscription)': '12 mois (abonnement annuel)',
          'Loading countries...': 'Chargement des pays...',
          'Back to Countries List': 'Retour à la liste des pays',
          'Flag': 'Drapeau',
          'Configure': 'Configurer',
          'Identity Management': 'Gestion des Identités',
          'Control account access and verify identity details': 'Contrôler l\'accès au compte et vérifier les détails d\'identité',
          'Search via name, email, or shop...': 'Rechercher par nom, e-mail ou boutique...',
          'platform users': 'utilisateurs de la plateforme',
          'User Profile': 'Profil de l\'utilisateur',
          'Account Details': 'Détails du Compte',
          'Associated Business': 'Entreprise Associée',
          'Shop Name': 'Nom de la Boutique',
          'Shop ID': 'ID de la Boutique',
          'Invite Platform Administrator': 'Inviter un administrateur de la plateforme',
          'Platform-Level Accounts Only': 'Comptes de niveau plateforme uniquement',
          'Creating users here registers them under the main platform administration context. To add shop-specific employees, please manage them within the respective shop dashboard.': 'La création d\'utilisateurs ici les enregistre sous le contexte d\'administration de la plateforme principale. Pour ajouter des employés spécifiques à une boutique, veuillez les gérer dans le tableau de bord de la boutique concernée.',
          'First name is required': 'Le prénom est requis',
          'Last name is required': 'Le nom de famille est requis',
          'Username is required': 'Le nom d\'utilisateur est requis',
          'Administrative Role': 'Rôle administratif',
          'SUPER ADMIN': 'SUPER ADMIN',
          'PLATFORM OPERATOR': 'OPÉRATEUR DE PLATEFORME',
          'Send Platform Invitation': 'Envoyer l\'invitation sur la plateforme',
          'Edit User Profile': 'Modifier le profil de l\'utilisateur',
          'Update User Details': 'Mettre à jour les détails de l\'utilisateur',
          'User invited successfully': 'Utilisateur invité avec succès',
          'Failed to invite user': 'Échec de l\'invitation de l\'utilisateur',
          'User profile updated successfully': 'Profil utilisateur mis à jour avec succès',
          'Failed to update user profile': 'Échec de la mise à jour du profil utilisateur',
          'Password reset email sent (simulation)': 'E-mail de réinitialisation du mot de passe envoyé (simulation)',
          'Delete User?': 'Supprimer l\'utilisateur ?',
          'This will permanently remove the user from the platform.': 'Cela supprimera définitivement l\'utilisateur de la plateforme.',
          'Delete': 'Supprimer',
          'Deletion API pending integration - using Mock': 'API de suppression en attente d\'intégration - utilisation de Mock',
          'IDENTITY': 'IDENTITÉ',
          'ACCESS ROLE': 'RÔLE D\'ACCÈS',
          'ASSIGNED SHOP': 'BOUTIQUE ASSIGNÉE',
          'CONTACT': 'CONTACT',
          'Unset': 'Non défini',
          'JOINED': 'REJOINT LE',
          'Revoke Access': 'Révoquer l\'accès',
          'Grant Access': 'Accorder l\'accès',
          'Revoke Access?': 'Révoquer l\'accès ?',
          'Grant Access?': 'Accorder l\'accès ?',
          'Edit Profile': 'Modifier le profil',
          'Reset Password': 'Réinitialiser le mot de passe',
          'Suspend Account': 'Suspendre le compte',
          'Reactivate Account': 'Réactiver le compte',
          'BUSINESS NAME': 'NOM DE L\'ENTREPRISE',
          'INDUSTRY': 'SECTEUR D\'ACTIVITÉ',
          'SUBSCRIPTION': 'ABONNEMENT',
          'Expires': 'Expire le',
          'OPERATOR': 'OPÉRATEUR',
          'ONBOARDED': 'INSCRIT LE',
          'View Details': 'Voir les détails',
          'Edit Shop Details': 'Modifier les détails de la boutique',
          'Renew Subscription (Cash)': 'Renouveler l\'abonnement (Espèces)',
          'Suspend Business': 'Suspendre l\'entreprise',
          'Reactivate Business': 'Réactiver l\'entreprise',
          'Delete Shop': 'Supprimer la boutique',
          'Are you sure delete this shop?': 'Êtes-vous sûr de vouloir supprimer cette boutique ?',
          'This action cannot be undone.': 'Cette action ne peut pas être annulée.',
          'Yes, Delete': 'Oui, supprimer',
          'Full edit form coming soon': 'Formulaire d\'édition complet disponible bientôt',
          'My Business': 'Mon entreprise',
          'Active Subscription': 'Abonnement actif',
          'Your workspace is active and fully operational.': 'Votre espace de travail est actif et pleinement opérationnel.',
          'Code': 'Code',
          'Expires At': 'Expire le',
          'Your workspace license has expired. Only the owner or administrator of this business can renew the subscription.': 'Votre licence a expiré. Seul le propriétaire ou l\'administrateur peut renouveler l\'abonnement.',
          'Manage Extra Permissions': 'Gérer les autorisations supplémentaires',
          'These per-user overrides will grant or revoke specific access regardless of the assigned roles.': 'Ces surcharges par utilisateur accorderont ou révoqueront des accès spécifiques indépendamment des rôles attribués.',
          'Template': 'Modèle',
          'Override': 'Surcharge',
          'Role': 'Rôle',
          'On': 'Actif',
          'Hospital Expense Ledger': 'Grand livre des dépenses hospitalières',
          'Track medical supplies, ward maintenance, doctor fees, and hospital operating costs.': 'Suivez les fournitures médicales, l\'entretien des services, les honoraires des médecins et les coûts d\'exploitation de l\'hôpital.',
          'Clinical Expenses': 'Dépenses cliniques',
          'Total Hospital Expenses': 'Dépenses hospitalières totales',
          'Operating Invoices': 'Factures d\'exploitation',
          'Hospital Treasury Balance': 'Solde de la trésorerie hospitalière',
          'Total recorded expense vouchers': 'Total des justificatifs de dépenses enregistrés',
          'Expense Categories': 'Catégories de dépenses',
          'Hospital Payroll Ledger': 'Grand livre des salaires de l\'hôpital',
          'Manage medical staff compensation, nurse shifts, doctor call fees, and hospital contracts.': 'Gérer la rémunération du personnel médical, les gardes des infirmiers, les honoraires de garde des médecins et les contrats hospitaliers.',
          'Active Medical Staff': 'Personnel médical actif',
          'Active contracts': 'Contrats actifs',
          'Total Medical Compensation (Year)': 'Rémunération médicale totale (Année)',
          'Pending Salary Advances': 'Avances sur salaire en attente',
          'Total Disbursements': 'Décaissements totaux',
          'Staff by Department': 'Personnel par département',
          'Recent Activity': 'Activité récente',
          'New Advance': 'Nouvelle avance',
          'Payout Report': 'Rapport de paie',
          'Bulk Run': 'Exécution groupée',
          'Generate Individual': 'Générer individuel',
          'Medical & Staff Directory': 'Annuaire du personnel médical',
          'Manage hospital doctor profiles, nurse shifts, active contracts, and department assignments.': 'Gérer les profils des médecins, les gardes des infirmiers, les contrats actifs et les affectations de services.',
          'Add Medical Staff': 'Ajouter du personnel médical',
          'Record Expense': 'Enregistrer une dépense',
          'Record Hospital Expense': 'Enregistrer la dépense hospitalière',
          'Add Category': 'Ajouter une catégorie',
          'Edit Category': 'Modifier la catégorie',
          'Category Name': 'Nom de la catégorie',
          'Supplier / Payee': 'Fournisseur / Bénéficiaire',
          'Clinical Department / Expense Center': 'Département clinique / Centre de dépenses',
          'Select hospital department': 'Sélectionner le département hospitalier',
          'Expense Amount': 'Montant de la dépense',
          'Add': 'Ajouter',
          'Update': 'Modifier',
          'Invoice & Receipt Statement': 'Relevé de facture et reçu',
          'Official billing statement details': 'Détails du relevé de facturation officiel',
          'Invoice No': 'N° de facture',
          'Total Bill': 'Facture totale',
          'Patient / Client': 'Patient / Client',
          'Issue Date': 'Date d\'émission',
          'Patient & Insurance Details': 'Détails du patient & de l\'assurance',
          'Insurance Coverage': 'Couverture d\'assurance',
          'Covered Amount': 'Montant couvert',
          'Statement Summary': 'Résumé du relevé',
          'Remaining Balance': 'Solde restant',
          'Standard Consultation Fee': 'Frais de consultation standard',
          'Walking Customer': 'Client de passage',
          'No Insurance - Private Patient': 'Sans assurance - Patient privé',
          'Amount Paid': 'Montant payé',
          'Subtotal': 'Sous-total',
          '1 Unit': '1 Unité',
          'Units': 'Unités',
          'Thermal Print': 'Impression thermique',
          'Save as PDF': 'Enregistrer en PDF',
          'Record Payment': 'Enregistrer le paiement',
          'Choose staff member': 'Choisir un membre du personnel',
          'Payroll Period': 'Période de paie',
          'Our smart system will automatically detect any approved salary advances and deduct them from this payroll.': 'Notre système intelligent détectera automatiquement toutes les avances sur salaire approuvées et les déduira de cette paie.',
          'Internal notes for this payment...': 'Notes internes pour ce paiement...',
          'Enterprise Bulk Generation': 'Génération groupée d\'entreprise',
          'Start Bulk Processing': 'Démarrer le traitement groupé',
          'This will generate unique payroll records for all ACTIVE employees. The system will respect individual base salaries and calculate advance deductions automatically.': 'Cela générera des fiches de paie uniques pour tous les employés ACTIFS. Le système respectera les salaires de base individuels et calculera automatiquement les déductions d\'avance.',
          'Processing Period': 'Période de traitement',
          'Disburse Salary Advance': 'Verser une avance sur salaire',
          'Recipient': 'Bénéficiaire',
          'Disbursement Amount': 'Montant du versement',
          'Purpose': 'Motif',
          'E.g. Medical, Rent, School Fees...': 'Ex. Médical, Loyer, Frais de scolarité...',
          'Heads Up: This advance will be tracked and linked to the next payroll generated for this employee.': 'Attention : cette avance sera suivie et liée à la prochaine fiche de paie générée pour cet employé.',
          'Global Payout Summary': 'Résumé global des paiements',
          'Print Review': 'Imprimer l\'aperçu',
          'Institutional Payout Summary': 'Résumé des paiements institutionnels',
          'Staff Code': 'Code du personnel',
          'Full Name': 'Nom complet',
          'Net Payment': 'Paiement net',
          'Total Staff': 'Effectif total',
          'Grand Total': 'Total général',
          'Individual Salary Statement': 'Fiche de salaire individuelle',
          'Got it': 'Compris',
          'Employee Information': 'Informations de l\'employé',
          'Earnings': 'Gains',
          'Regular Pay': 'Salaire régulier',
          'Bonus / Overtime': 'Prime / Heures supplémentaires',
          'Tax / Pension': 'Impôt / Retraite',
          'NET TAKE-HOME': 'NET À PAYER',
          'Confirm Disbursement Source': 'Confirmer la source du versement',
          'Select Fund Source': 'Sélectionner la source des fonds',
          'Doctor, Nurse, Pharmacist...': 'Médecin, Infirmier, Pharmacien...',
          'OPD, IPD, Pharmacy...': 'OPD, IPD, Pharmacie...',
          'Create': 'Créer',
          'First Name': 'Prénom',
          'Last Name': 'Nom de famille',
          'Base Salary': 'Salaire de base',
          'Hire Date': 'Date d\'embauche',
          'Work Status': 'Statut d\'activité',
          'On Leave': 'En congé',
          'Inactive': 'Inactif',
          'Create Employee Record': 'Créer une fiche d\'employé',
          'Edit Employee Record': 'Modifier la fiche d\'employé',
          'No payroll history found': 'Aucun historique de paie trouvé',
          'Generated payroll records will appear here.': 'Les fiches de paie générées apparaîtront ici.',
          'No advance records found': 'Aucune avance sur salaire trouvée',
          'Payments issued will appear here automatically.': 'Les paiements effectués apparaîtront ici automatiquement.',
          'Create Smart Payroll': 'Créer une fiche de paie',
          'Select Employee': 'Sélectionner l\'employé',
          'Bonus / Allowances': 'Prime / Indemnités',
          'Other Deductions': 'Autres déductions',
          'Select Period': 'Sélectionner la période',
          'Export Payout Sheet': 'Exporter la feuille de paiement',
          'Save Changes': 'Enregistrer les modifications',
          'Edit Payroll Record': 'Modifier la fiche de paie',
          'Smart Payroll Hub': 'Hub de paie intelligent',
          'Enterprise-grade compensation management with multi-tenant isolation': 'Gestion des rémunérations d\'entreprise avec isolation multi-locataire',
          'Payroll History': 'Historique de paie',
          'Salary Advances': 'Avances sur salaire',
          'Awaiting review': 'En attente d\'examen',
          'Active run payments': 'Paiements de la session active',
          'Cumulative paid': 'Cumul des paiements',
          'Medical Staff by Department': 'Personnel médical par département',
          'Staff Name': 'Nom du personnel',
          'Date Requested': 'Date de demande',
          'Deducted': 'Déduit',
          'Approve': 'Approuver',
          'Reject': 'Rejeter',
          'Outpatient Department (OPD)': 'Service des consultations externes (OPD)',
          'Inpatient Wards (IPD)': 'Salles d\'hospitalisation (IPD)',
          'Pharmacy Department': 'Service de pharmacie',
          'Laboratory & Diagnostics': 'Laboratoire & Diagnostic',
          'Operation Theatre (OT)': 'Bloc opératoire (OT)',
          'Emergency Room (ER)': 'Urgences (ER)',
          'General Facility & Administration': 'Services généraux & Administration',
          'Outpatient (OPD)': 'Consultation externe (OPD)',
          'Inpatient (IPD)': 'Hospitalisation (IPD)',
          'Pharmacy': 'Pharmacie',
          'Administration': 'Administration',
          'Clinical Dept': 'Département clinique',
          'Clinical Expense Records': 'Registre des dépenses cliniques',
          'Expense History': 'Historique des dépenses',
          'Please enter amount': 'Veuillez entrer le montant',
          
          // ─── Permission Feature Names (from backend) ────────────────────
          'Store Fulfillment': 'Fulfillment du magasin',
          'Sales Analytics': 'Analyses des ventes',
          'Sales History': 'Historique des ventes',
          'Inventory': 'Inventaire',
          'Funds': 'Fonds',
          'Products': 'Produits',
          'Customers': 'Clients',
          'Expenses': 'Dépenses',
          'Archived Stock': 'Stock archivé',
          'Manage Branches': 'Gérer les succursales',
          'Stock': 'Stock',
          'Wholesale': 'Vente en gros',
          'Purchase Orders': 'Bons de commande',
          'Delivery Notes': 'Bons de livraison',
          'Pharmacy POS': 'POS Pharmacie',
          'Pharmacy Dashboard': 'Tableau de bord Pharmacie',
          'Dispensing': 'Dispensation',
          'Prescriptions': 'Ordonnances',
          'Patients': 'Patients',
          'Medications': 'Médicaments',
          'Pharmacists': 'Pharmaciens',
          'Batches & Expiry': 'Lots & Expiration',
          'Pharmacy Reports': 'Rapports Pharmacie',
          'Pharmacy Procurement': 'Approvisionnement Pharmacie',
          'Pharmacy Branches': 'Succursales Pharmacie',
          'Pharmacy Stock Transfers': 'Transferts de stock Pharmacie',
          'Goods Receiving': 'Réception de marchandises',
          'Hospital Dashboard': 'Tableau de bord Hôpital',
          "Hospital Queue": "File d'attente Hôpital",
          'Hospital Appointments': 'Rendez-vous Hôpital',
          'Hospital Triage': 'Triage Hôpital',
          'Hospital Consultation': 'Consultation Hôpital',
          'Hospital Wards': 'Salles Hôpital',
          'Hospital Labs': 'Laboratoires Hôpital',
          'Hospital Finance': 'Finance Hôpital',
          'Hospital PACS': 'PACS Hôpital',
          'Hospital Settings': 'Paramètres Hôpital',
          'Bar POS': 'POS Bar',
          'Bar Dashboard': 'Tableau de bord Bar',
          'Bar Stock': 'Stock Bar',
          'Bar Recipes': 'Recettes Bar',
          'Bar Tabs': 'Ardoises Bar',
          'Bar Shifts': 'Quarts de travail Bar',
          'Bar Analytics': 'Analyses Bar',
          'Bar Menus': 'Menus Bar',
          'Restaurant Dashboard': 'Tableau de bord Restaurant',
          'Restaurant Tables': 'Tables Restaurant',
          'Restaurant Reservations': 'Réservations Restaurant',
          'Restaurant Kitchen': 'Cuisine Restaurant',
          'HRM': 'Gestion RH',
          'Settings': 'Paramètres',
          // Mobile Money & Agency Banking
          'Mobile Money & Agency Banking': 'Mobile Money & Services Financiers',
          'Mobile Money': 'Mobile Money',
          'Agency Banking': 'Agence Bancaire',
          'Mobile Money Agent': 'Agent Mobile Money',
          'Agent Terminal POS': 'Terminal POS Agent',
          'E-Floats & Cash Till': 'Solde Électronique & Caisse',
          'Digital Ledger': 'Registre Numérique',
          'Shift Audit & Close': 'Clôture de Caisse & Audit',
          'Cash-In (Deposit)': 'Dépôt (Cash-In)',
          'Cash-Out (Withdrawal)': 'Retrait (Cash-Out)',
          'Airtime Top-Up': 'Recharge Crédit (Airtime)',
          'Bill & Utility Payment': 'Paiement de Factures',
          'Physical Cash in Drawer': 'Espèces en Caisse',
          'Total Digital E-Float Balance': 'Solde Électronique Total',
          'Earned Agent Commissions': 'Commissions Gagnées',
          'Rebalance Float': 'Rééquilibrer la Trésorerie',

          // ─── Permission Feature Descriptions (from backend) ──────────────
          'Permission to manage store delivery and fulfillment': 'Autorisation de gérer la livraison et le fulfillment du magasin',
          'Permission to view sales analytics': 'Autorisation de consulter les analyses de ventes',
          'Permission to view financial analytics': 'Autorisation de consulter les analyses financières',
          'View detailed history of all sales transactions': 'Voir l\'historique détaillé de toutes les transactions de ventes',
          'Provides an overview of key metrics, activities, and summaries of the system in a central location.': 'Fournit un aperçu des indicateurs clés, des activités et des résumés du système dans un emplacement central.',
          'Manages and tracks stock levels, product availability, and items in the warehouse.': 'Gère et suit les niveaux de stock, la disponibilité des produits et les articles en entrepôt.',
          'Tracks sales transactions, revenue generation, and customer purchases.': 'Suit les transactions de vente, la génération de revenus et les achats des clients.',
          'Monitors available funds, cash flow, and financial balances.': 'Surveille les fonds disponibles, les flux de trésorerie et les soldes financiers.',
          'Catalogs all available products in the system, including medications and general items.': 'Répertorie tous les produits disponibles dans le système, y compris les médicaments et les articles généraux.',
          'Manages customer profiles, purchase history, and customer relationships.': 'Gère les profils clients, l\'historique d\'achat et les relations clients.',
          'Tracks and manages business expenses and operational costs.': 'Suit et gère les dépenses commerciales et les coûts opérationnels.',
          'Manages archived and discontinued stock items.': 'Gère les articles de stock archivés et discontinués.',
          'Manages supplier information and relationships.': 'Gère les informations et les relations avec les fournisseurs.',
          'Manages multiple business branches and locations.': 'Gère plusieurs succursales et emplacements commerciaux.',
          'Manages stock levels and inventory operations.': 'Gère les niveaux de stock et les opérations d\'inventaire.',
          'Manages bulk sales and wholesale transactions.': 'Gère les ventes en volume et les transactions en gros.',
          'Manages purchase orders from suppliers.': 'Gère les bons de commande auprès des fournisseurs.',
          'Manages delivery notes for outgoing shipments.': 'Gère les bons de livraison pour les expéditions sortantes.',
          'Point of sale system for pharmacy sales transactions.': 'Système de point de vente pour les transactions de la pharmacie.',
          'Dashboard for pharmacy operations overview.': 'Tableau de bord pour une vue d\'ensemble des opérations de la pharmacie.',
          'Manages medication dispensing to patients.': 'Gère la dispensation de médicaments aux patients.',
          'Manages medical prescriptions.': 'Gère les ordonnances médicales.',
          'Manages patient records and information.': 'Gère les dossiers et informations des patients.',
          'Manages medication inventory and catalog.': 'Gère l\'inventaire et le catalogue de médicaments.',
          'Manages pharmacist staff records.': 'Gère les dossiers du personnel pharmacien.',
          'Tracks medication batches and expiry dates.': 'Suit les lots de médicaments et les dates d\'expiration.',
          'Pharmacy sales and operational reports.': 'Rapports de ventes et opérationnels de la pharmacie.',
          'Manages pharmacy procurement and purchase orders.': 'Gère l\'approvisionnement et les bons de commande de la pharmacie.',
          'Manages pharmacy branch locations.': 'Gère les succursales de la pharmacie.',
          'Manages stock transfers between pharmacy branches.': 'Gère les transferts de stock entre les succursales de la pharmacie.',
          'Manages incoming goods and stock receiving.': 'Gère la réception des marchandises et du stock entrant.',
          'Hospital management and clinical operations dashboard.': 'Tableau de bord de gestion hospitalière et des opérations cliniques.',
          'Manages patient appointment scheduling.': 'Gère la planification des rendez-vous patients.',
          'Manages patient triage and priority assessment.': 'Gère le triage des patients et l\'évaluation des priorités.',
          'Manages doctor-patient consultations.': 'Gère les consultations médecin-patient.',
          'Manages hospital ward assignments and bed management.': 'Gère les affectations des salles et la gestion des lits.',
          'Manages laboratory tests and results.': 'Gère les tests de laboratoire et les résultats.',
          'Manages hospital financial operations and billing.': 'Gère les opérations financières et la facturation de l\'hôpital.',
          'Manages medical imaging and PACS system.': 'Gère l\'imagerie médicale et le système PACS.',
          'Hospital system configuration and settings.': 'Configuration et paramètres du système hospitalier.',
          'Hospital operational and financial reports.': 'Rapports opérationnels et financiers de l\'hôpital.',
          'Point of sale system for bar operations.': 'Système de point de vente pour les opérations de bar.',
          'Bar management overview and statistics.': 'Vue d\'ensemble et statistiques de gestion de bar.',
          'Manages bar inventory and beverage stock.': 'Gère l\'inventaire du bar et le stock de boissons.',
          'Manages cocktail and drink recipes.': 'Gère les recettes de cocktails et de boissons.',
          'Manages customer bar tabs and billing.': 'Gère les ardoises et la facturation des clients.',
          'Manages staff shifts and scheduling.': 'Gère les quarts de travail et la planification du personnel.',
          'Manages restaurant tables and seating.': 'Gère les tables et les places du restaurant.',
          'Manages restaurant reservations.': 'Gère les réservations du restaurant.',
          'Manages kitchen orders and food preparation.': 'Gère les commandes de cuisine et la préparation des aliments.',
          'Human resources management and employee records.': 'Gestion des ressources humaines et dossiers des employés.',
          'Manages employee payroll and compensation.': 'Gère la paie et la rémunération des employés.',
          'Tracks system activity and changes for compliance.': 'Suit l\'activité et les modifications du système pour la conformité.',
          'Business performance reports and summaries.': 'Rapports et résumés des performances commerciales.',
          'System configuration and business settings.': 'Configuration du système et paramètres commerciaux.',
          'Analytics for customer behavior and trends.': 'Analyses du comportement et des tendances des clients.',
          'Analytics for inventory levels and movements.': 'Analyses des niveaux et des mouvements d\'inventaire.',
          'Tracks stock movement history in and out of the warehouse.': 'Suit l\'historique des mouvements de stock entrants et sortants.',
          'Patient visit and medical history records.': 'Dossiers des visites et antécédents médicaux des patients.',
          'Access Restricted': 'Accès restreint',
          'Your account does not have the permissions required to view this module. Please contact your system administrator if you believe this is an error.': "Votre compte ne dispose pas des autorisations requises pour afficher ce module. Veuillez contacter votre administrateur système si vous pensez qu'il s'agit d'une erreur.",
          'Go Back': 'Retour',
          'Go to Dashboard': 'Aller au tableau de bord',
          'Proceed to Checkout': 'Passer à la caisse',
          'Confirm Payment': 'Confirmer le paiement',
          'Sale Type': 'Type de vente',
          'All Items': 'Tous les articles',
          'Analgesic': 'Analgésiques',
          'Antibiotic': 'Antibiotiques',
          'Vitamin': 'Vitamines',
          'Antipyretic': 'Antipyrétiques',
          'Antacid': 'Anti-acides',
          'Out of Stock': 'En rupture de stock',
          'Base': 'Base',
          'Piece': 'Pièce',
          'Box': 'Boîte',
          'Unit': 'Unité',
          'Detailed Inventory': 'Inventaire Détaillé',
          'Inventory Overview': "Aperçu de l'inventaire",
          'Dashboard': 'Tableau de bord',
          'Sales': 'Ventes',
          'Retail': 'Commerce de détail',
          'Smart Management for Modern Businesses': 'Gestion intelligente pour les entreprises modernes',
          'Empower your team, manage stores, track sales, and streamline operations in one simple, unified platform.': "Autonomisez votre équipe, gérez vos magasins, suivez vos ventes et simplifiez vos opérations au sein d'une seule et même plateforme.",
          'Analytics Dashboard': 'Tableau de bord analytique',
          'Active': 'Actif',
          'Shop, Supermarket & Store': 'Boutique, Supermarché & Magasin',
          // Stepper & Layout
          'Business': 'Entreprise',
          'Contact': 'Contact',
          'Security': 'Sécurité',
          'Account Created!': 'Compte créé !',
          'Sign In Now': 'Se connecter maintenant',
          'Create your account': 'Créer votre compte',
          'Step': 'Étape',
          'of': 'sur',
          'Continue': 'Continuer',
          'Back': 'Retour',
          'Create Account': 'Créer le compte',
          'Sign In': 'Se connecter',
          'By continuing, you agree to our': 'En continuant, vous acceptez nos',
          'Terms': "Conditions d'utilisation",
          'and': 'et',
          'Privacy Policy': 'Politique de confidentialité',

          // Login Page
          'Welcome back': 'Bon retour',
          'Please enter your details to sign in.': 'Veuillez entrer vos identifiants pour vous connecter.',
          "Don't have an account?": "Vous n'avez pas de compte ?",
          'Create an account': 'Créer un compte',
          'Need help?': "Besoin d'aide ?",
          'Please input your password': 'Veuillez entrer votre mot de passe',
          'Enter your password': 'Entrer votre mot de passe',
          'Back to Login': 'Retour à la connexion',
          'Set Password & Continue': 'Définir le mot de passe & continuer',
          'Verification Code (OTP)': 'Code de vérification (OTP)',
          'Enter the OTP sent to your phone or email': "Entrez l'OTP envoyé sur votre téléphone ou e-mail",
          'New Password': 'Nouveau mot de passe',
          'Please input your password!': 'Veuillez entrer votre mot de passe !',
          'Password must be at least 8 characters long and include uppercase letters, lowercase letters, and numbers.': 'Le mot de passe doit comporter au moins 8 caractères et inclure des lettres majuscules, des lettres minuscules et des chiffres.',
          'Create a strong password': 'Créer un mot de passe fort',
          'Please confirm your password!': 'Veuillez confirmer votre mot de passe !',
          'Re-enter your new password': 'Réentrer votre nouveau mot de passe',
          'At least 8 characters': 'Au moins 8 caractères',
          'One uppercase letter': 'Une lettre majuscule',
          'One lowercase letter': 'Une lettre minuscule',
          'One number': 'Un chiffre',
          'Set Your New Password': 'Définissez votre nouveau mot de passe',
          'For security, please create a new password before continuing.': 'Par sécurité, veuillez créer un nouveau mot de passe avant de continuer.',

          // Step 1: Business Info
          'Loading business options...': "Chargement des types d'entreprise...",
          'e.g. Acme Pharmacy': 'ex. Pharmacie Acme',
          'Business Type': "Type d'entreprise",
          'Please select a business type': "Veuillez sélectionner un type d'entreprise",
          'Coffee & Drinks': 'Café & Boissons',
          'Retail & Boutique': 'Boutique & Commerce',
          'Retail & Commerce': 'Boutique & Commerce',
          'Pharmacy & Healthcare': 'Pharmacie & Santé',
          'Restaurant & Food': 'Restaurant & Alimentation',
          'Hospital & Clinic': 'Hôpital & Clinique',
          'Medical Clinic': 'Clinique Médicale',
          'Clinic': 'Clinique',
          'Hotel & Hospitality': 'Hôtel & Hébergement',
          'Bar & Lounge': 'Bar & Lounge',
          'Hardware & Materials': 'Quincaillerie & Matériaux',
          'Mobile Money & Services': 'Mobile Money & Services',
          'Shoes & Footwear': 'Magasin de Chaussures',
          'Shoe Store & Footwear': 'Magasin de Chaussures',
          'Hair Salon & Beauty': 'Salon de Coiffure & Beauté',
          'Barbershop & Grooming': 'Barbershop & Soins Homme',
          'Real Estate & Property': 'Immobilier & Propriétés',
          'Spa & Wellness': 'Spa & Bien-être',
          'Tailoring & Custom Clothing': 'Couture & Vêtements sur Mesure',
          'Car Rental & Vehicles': 'Location de Véhicules & Flotte',
          'Car Rental & Fleet': 'Location de Véhicules & Flotte',
          'Photography & Video': 'Photographie & Studio Média',
          'Photography & Media Studio': 'Photographie & Studio Média',
          'Professional Services & Consulting': 'Services Professionnels & Conseil',
          'High-Tech & Electronics': 'Électronique & High-Tech',
          'Grocery & Supermarket': 'Épicerie & Supermarché',

          // Direct French Presets Identity (Guarantees clean FR display when French is active)
          'Café & Boissons': 'Café & Boissons',
          'Boutique & Commerce': 'Boutique & Commerce',
          'Pharmacie & Santé': 'Pharmacie & Santé',
          'Restaurant & Alimentation': 'Restaurant & Alimentation',
          'Hôpital & Clinique': 'Hôpital & Clinique',
          'Hôtel & Hébergement': 'Hôtel & Hébergement',
          'Quincaillerie & Matériaux': 'Quincaillerie & Matériaux',
          'Magasin de Chaussures': 'Magasin de Chaussures',
          'Services Professionnels & Conseil': 'Services Professionnels & Conseil',

          // Step 2: Contact Info
          'Email Address': 'Adresse e-mail',
          'Enter a valid email': 'Entrez un e-mail valide',
          'Country': 'Pays',
          'Phone Number': 'Numéro de téléphone',
          'Numbers only': 'Chiffres uniquement',

          // Step 3: Password & Security
          'Passwords do not match!': 'Les mots de passe ne correspondent pas !',
          'Min 8 chars, uppercase, lowercase & number': 'Min 8 caractères, majuscule, minuscule & chiffre',
          'Create a password': 'Créer un mot de passe',
          'Weak': 'Faible',
          'Fair': 'Moyen',
          'Good': 'Bon',
          'Strong': 'Fort',
          'Repeat your password': 'Répétez votre mot de passe',
          'Password must include': 'Le mot de passe doit inclure',
          '8+ characters': '8+ caractères',
          'Uppercase letter': 'Lettre majuscule',
          'Lowercase letter': 'Lettre minuscule',
          'Number': 'Chiffre',
          'Suppliers': 'Fournisseurs',
          'Reports': 'Rapports',
          'Products list': 'Liste des produits',
          'Step 2 of 2: Business Details': "Étape 2 sur 2 : Détails de l'entreprise",
          'Business Name': "Nom de l'entreprise",
          'Work Email': 'Email professionnel',
          'Password': 'Mot de passe',
          'Confirm Password': 'Confirmer le mot de passe',
          'Already have an account?': 'Vous avez déjà un compte ?',
          'Log in': 'Se connecter',
          'Welcome to FUKKONI': 'Bienvenue chez FUKKONI',
          'Your account has been successfully created. Please log in with your credentials.': 'Votre compte a été créé avec succès. Veuillez vous connecter avec vos identifiants.',
          'Total Revenue': "Chiffre d'affaires total",
          'Transactions': 'Transactions',
          'Avg Transaction': 'Transaction Moyenne',
          'Total Profit': 'Bénéfice Total',
          'Sales Report': 'Rapport des Ventes',
          'Top Performing Products': 'Produits les Plus Performants',
          'Units Sold': 'Unités Vendues',
          'Revenue': 'Revenu',
          'Profit': 'Bénéfice',
          'Margin': 'Marge',
          'Inventory Valuation': 'Valorisation des Stocks',
          'Inventory Value by Category': 'Valeur du Stock par Catégorie',
          'Detailed Per-Item Valuation': 'Valorisation Détaillée par Article',
          'Profit Analysis': 'Analyse des Bénéfices',
          'Profit Breakdown by Category': 'Répartition des Bénéfices par Catégorie',
          'Category Performance': 'Performance par Catégorie',
          'Category Performance Metrics': 'Mesures de Performance par Catégorie',
          'Contribution': 'Contribution',
          'Items Sold': 'Articles Vendus',
          'Bill Patient': 'Facturer le Patient',
          'BILLED': 'FACTURÉ',
          'Billing': 'Facturation',
          'Consultation Room': 'Salle de Consultation',
          'Generate Clinical Bill': 'Générer une Facture Clinique',
          'Generate Invoice': 'Générer la Facture',
          'Sign & Lock': 'Signer et Verrouiller',
          'Billing order generated successfully': 'Commande de facturation générée avec succès',
          // Backend clinical & HMS success/error messages
          'Encounter not found': 'Rencontre non trouvée',
          'Encounter already billed': 'Rencontre déjà facturée',
          'Patient not found': 'Patient non trouvé',
          'Cannot update triage of a locked encounter': 'Impossible de mettre à jour le triage d\'une rencontre verrouillée',
          'Cannot update a locked encounter': 'Impossible de modifier une rencontre verrouillée',
          'Finalize & Lock Consultation?': 'Finaliser & Verrouiller la Consultation ?',
          'Once locked, clinical notes become immutable. Further changes will require addendums.': 'Une fois verrouillées, les notes cliniques deviennent immuables. Toute modification ultérieure nécessitera des addendas.',
          'EMERGENCY ACCESS OVERRIDE: Break the Glass': 'ACCÈS D\'URGENCE : Briser la Glace',
          'RESTRICTED VIP MEDICAL CHART': 'DOSSIER MÉDICAL VIP RESTREINT',
          'This clinical file is flagged as highly sensitive. Access is heavily restricted under HIPAA and GDPR compliance codes. Continuing will log your name, IP address, timestamp, and justification in the hospital security compliance ledger.': 'Ce dossier clinique est marqué comme hautement sensible. L\'accès est strictement restreint selon les normes HIPAA et RGPD. Continuer enregistrera votre nom, adresse IP, horodatage et justification dans le registre de sécurité de l\'hôpital.',
          'Provide Clinical Override Justification': 'Fournir la justification de la dérogation clinique',
          'e.g., Emergency consultation requested by primary physician, Direct trauma ward intake, Critical lab review': 'ex. Consultation d\'urgence demandée par le médecin principal, Entrée directe aux urgences, Analyse critique de laboratoire',
          'Break the Glass & Access': 'Briser la Glace & Accéder',
          'Justification Required': 'Justification requise',
          'You must provide a clinical reason to break the glass.': 'Vous devez fournir une raison clinique pour briser la glace.',
          'Access Approved': 'Accès approuvé',
          'Temporary access granted. Chart view is recorded for security compliance.': 'Accès temporaire accordé. La consultation du dossier est enregistrée pour contrôle de sécurité.',
          'Override Failed': 'Échec de la dérogation',
          'Failed to log emergency access justification.': 'Échec de l\'enregistrement de la justification d\'accès d\'urgence.',
          'Bed is not available': 'Le lit n\'est pas disponible',
          'Admission not found': 'Admission non trouvée',
          'Medical order not found': 'Ordonnance médicale non trouvée',
          'Cannot administer inactive medical order': 'Impossible d\'administrer une ordonnance médicale inactive',
          'PACS attachment not found': 'Pièce jointe PACS non trouvée',
          'Deposit amount must be greater than zero': 'Le montant du dépôt doit être supérieur à zéro',
          'No active deposit balance found for patient': 'Aucun solde de dépôt actif trouvé pour le patient',
          'Order not found': 'Commande non trouvée',
          'Order has no outstanding balance due': 'La commande n\'a aucun solde dû',
          'No deposit balance to refund': 'Aucun solde de dépôt à rembourser',
          // Sidebar sections & HMS items
          'HMS COMMAND CENTER': 'Centre de Contrôle HMS',
          'CLINICAL CARE (EMR)': 'Soins Cliniques (DME)',
          'DIAGNOSTICS & LABS': 'Diagnostics & Laboratoires',
          'HOSPITAL PHARMACY': 'Pharmacie Hospitalière',
          'BILLING & REVENUE': 'Facturation & Revenus',
          'HIS Dashboard': 'Tableau de Bord HIS',
          'EHR Dashboard': 'Tableau de Bord DME',
          'Clinical Director Console': 'Console du Directeur Clinique',
          'Appointments': 'Rendez-vous',
          'Patient Queue': 'File d\'Attente Patients',
          'Triage': 'Triage',
          'Consultation': 'Consultation',
          'Patient Records': 'Dossiers Patients',
          'Laboratory': 'Laboratoire',
          'Inpatient Wards': 'Services d\'Hospitalisation',
          'Billing & Escrow': 'Facturation & Consignation',
          'PACS Imaging': 'Imagerie PACS',
          'Hospital Reports': 'Rapports Hospitaliers',
          'Financial Analytics': 'Analyses Financières',
          'Hospital Pharmacy POS': 'Point de Vente Pharmacie',
          'Hospital Pharmacy Dashboard': 'Tableau de Bord Pharmacie',
          'Clinical Staff': 'Personnel Clinique',
          'Staff Management': 'Gestion du Personnel',
          'Employees': 'Employés',
          'Payroll': 'Paie',
          'General': 'Général',
          'Branches': 'Succursales',
          'Audit Logs': 'Journaux d\'Audit',
          // HIS Reports & Dashboard Overhaul
          'Hospital Census & Clinical Reports': 'Recensement Hospitalier & Rapports Cliniques',
          'Real-time inpatient census, outpatient consultation logs, lab orders, and copay records.': 'Recensement des patients hospitalisés, dossiers des consultations externes, ordonnances de laboratoire et dossiers de copaiement en temps réel.',
          'OPD Consultations': 'Consultations Externes (OPD)',
          'IPD Ward Occupancy': 'Occupation des Lits (IPD)',
          'Financial & Copay': 'Finances & Copaiement',
          'Laboratory Logs': 'Registres de Laboratoire',
          'Outpatient Department Consultation Logs': 'Registres des Consultations du Département Externe',
          'Inpatient Department Ward Census': 'Recensement des Services d\'Hospitalisation',
          'Financial Transactions & Copay Audits': 'Transactions Financières & Audits de Copaiement',
          'Laboratory Diagnostic Orders & Status': 'Commandes Diagnostics & Statuts du Laboratoire',
          'Search patient, doctor, or code...': 'Rechercher un patient, médecin ou code...',
          'Filter by Payer': 'Filtrer par Payeur',
          'Filter by Ward': 'Filtrer par Service',
          'Filter by Test Status': 'Filtrer par Statut du Test',
          'All Payers': 'Tous les Payeurs',
          'All Wards': 'Tous les Services',
          'All Statuses': 'Tous les Statuts',
          'Export CSV': 'Exporter en CSV',
          'Print PDF': 'Imprimer en PDF',
          'Financial Reports': 'Rapports Financiers',
          'Laboratory (LIS)': 'Laboratoire (LIS)',
          'Hospital Reports & Analytics': 'Rapports et Analyses de l\'Hôpital',
          'Performances, clinical insights, and financial trends.': 'Performances, analyses cliniques et tendances financières.',
          'Daily Patient Visits Volume': 'Volume Quotidien des Visites de Patients',
          'Visits': 'Visites',
          'Consultation Insights': 'Aperçu des Consultations',
          'Total Billed Revenue': 'Revenu Total Facturé',
          'Cash Portions': 'Part en Espèces',
          'Settled / Payments Received': 'Règlements / Paiements Reçus',
          'Abnormal Result Flag Distributions': 'Distribution des Résultats Anormaux',
          'Lab Request Volume by Status': 'Volume de Demandes de Laboratoire par Statut',
          'Results Count': 'Nombre de Résultats',
          'No abnormal flags data recorded': 'Aucune donnée de résultat anormal enregistrée',
          'Lab Request Volume by Test Type': 'Volume de Demandes par Type de Test',
          'Patient Encounters': 'Visites de Patients',
          'Hospital Revenue': 'Revenu de l\'Hôpital',
          'Low Stock Meds': 'Médicaments Faible Stock',
          'Sample Collected': 'Échantillon Collecté',
          'In Progress': 'En Cours',
          'Ward Type': 'Type de Salle',
          'Occupied Beds': 'Lits Occupés',
          'Total Beds': 'Total de Lits',
          'No data available to export': 'Aucune donnée disponible pour l\'exportation',
          'Excel/CSV spreadsheet downloaded successfully!': 'Tableur Excel/CSV téléchargé avec succès !',
          'Generating report PDF bundle...': 'Génération du document PDF du rapport...',
          'PDF Report generated! Opening print preview...': 'Rapport PDF généré ! Ouverture de l\'aperçu avant impression...',
          'ESI-1 Resuscitation': 'ESI-1 Réanimation',
          'ESI-2 Emergent': 'ESI-2 Urgent (Émergent)',
          'ESI-3 Urgent': 'ESI-3 Urgent',
          'ESI-4 Semi-Urgent': 'ESI-4 Semi-Urgent',
          'ESI-5 Non-Urgent': 'ESI-5 Non-Urgent',
          'Processing': 'Traitement en Cours',
          'Unpaid': 'Non Payé',
          'Pending Result': 'Résultat en Attente',
          'Ward': 'Service',
          'Bed No': 'Lit N°',
          'Admitting Diagnosis': 'Diagnostic d\'Admission',
          'Admission Date': 'Date d\'Admission',
          'Est. Discharge': 'Sortie Prévue',
          'Observations': 'Observations',
          'Payer': 'Payeur',
          'Insurance Share': 'Part Assurance',
          'Cash Share': 'Part Espèces',
          'Copay Status': 'Statut Copaiement',
          'Total Invoice': 'Facture Totale',
          'Test Name': 'Nom du Test',
          'Requesting Doc': 'Médecin Demandeur',
          'Result Value': 'Résultat',
          'Status': 'Statut',
          'Lab Sheet': 'Fiche de Labo',
          'Outpatient Consultation Record': 'Dossier de Consultation Externe',
          'Close': 'Fermer',
          'Basic Information': 'Informations de Base',
          'Encounter ID': 'ID Rencontre',
          'Clinical Metrics': 'Mesures Cliniques',
          'Chief Complaint': 'Plainte Principale',
          'None recorded': 'Aucune enregistrée',
          'Diagnosis': 'Diagnostic',
          'Not diagnosed yet': 'Non diagnostiqué encore',
          'Vitals & Triages': 'Signes Vitaux & Triages',
          'Systolic': 'Systolique',
          'Diastolic': 'Diastolique',
          'Pulse': 'Pouls',
          'Temp': 'Température',
          'Weight': 'Poids',
          'SpO2': 'SpO2',
          'Inpatient Admission Record': 'Dossier d\'Admission Hospitalisation',
          'Ward & Bed Allocation': 'Allocation Service & Lit',
          'Bed Number': 'Numéro de Lit',
          'Clinical Notes & Status': 'Notes Cliniques & Statuts',
          'Copay Audits & Ledger': 'Audits de Copaiement & Grand Livre',
          'Invoice Details': 'Détails de la Facture',
          'Payment Information': 'Informations de Paiement',
          'Laboratory Diagnosis Sheet': 'Fiche de Diagnostic de Laboratoire',
          'Pathology Test Details': 'Détails du Test de Pathologie',
          'Reference Range': 'Intervalle de Référence',
          'Clinical Signoff': 'Validation Clinique',
          'Approved': 'Approuvé',
          'Pending': 'En attente',
          'Normal': 'Normal',
          'Abnormal': 'Anormal',
          'Critical': 'Critique',
          'High': 'Élevé',
          'Low': 'Bas',

          // Clinical Case Headers & Details Modal
          'Clinical Case Sheet Summary': 'Résumé de la Fiche de Cas Clinique',
          'Inpatient Admission Register Record': 'Registre d\'Admission d\'Hospitalisation',
          'Invoice & Patient Ledger Statement': 'Facture & Relevé du Grand Livre Patient',
          'Laboratory Diagnostic Report': 'Rapport de Diagnostic de Laboratoire',

          // Scheduling & Rosters
          'Clinical Scheduling': 'Planification Clinique',
          'Roster doctor shifts and book outpatient consultations': 'Planifier les rotations des médecins et réserver des consultations externes',
          'Total Appointments': 'Total des Rendez-vous',
          'Checked In / Waiting': 'Enregistré / En Attente',
          'Pending Scheduled': 'Planification En Attente',
          'Cancelled Bookings': 'Réservations Annulées',
          'Appointments List': 'Liste des Rendez-vous',
          'Duty Shift Rosters': 'Rotations de Garde',
          'Define Shift Duty': 'Définir la Garde',
          'Configure weekly availability rules.': 'Configurer les règles de disponibilité hebdomadaire.',
          'Consulting Practitioner': 'Praticien Consultant',
          'Choose practitioner...': 'Choisir un praticien...',
          'Day of the Week': 'Jour de la Semaine',
          'Please select day': 'Veuillez sélectionner le jour',
          'Select day...': 'Sélectionner le jour...',
          'Shift Work Hours': 'Heures de Travail de Garde',
          'Please select hours': 'Veuillez sélectionner les heures',
          'Save Shift Rule': 'Enregistrer la Règle de Garde',
          'Active Duty Hours': 'Heures de Garde Actives',
          'Practitioner availability blocks configured for scheduling.': 'Blocs de disponibilité du praticien configurés pour la planification.',
          'Total Shifts': 'Total des Gardes',
          'Select a doctor to view and manage their duty roster.': 'Sélectionner un médecin pour afficher et gérer son planning de garde.',
          'Configure weekly shift hours to check booking availability.': 'Configurer les heures de garde hebdomadaires pour vérifier la disponibilité des réservations.',
          'No shift duties configured for this doctor': 'Aucune garde configurée pour ce médecin',
          'Filter by Consulting Doctor': 'Filtrer par Médecin Consultant',
          'No appointments scheduled': 'Aucun rendez-vous planifié',
          'Click Schedule Appointment to add your first patient consultation.': 'Cliquez sur Planifier un Rendez-vous pour ajouter votre première consultation de patient.',
          'Reschedule Consultation': 'Reprogrammer la Consultation',
          'Cancel Appointment': 'Annuler le Rendez-vous',
          'Yes': 'Oui',
          'No': 'Non',
          'Cancel this appointment?': 'Annuler ce rendez-vous ?',
          'Today': 'Aujourd\'hui',
          'Scheduled Time': 'Heure Prévue',
          'Patient checked in and added to clinical queue': 'Patient enregistré et ajouté à la file d\'attente clinique',
          'Failed to check in patient': 'Échec de l\'enregistrement du patient',
          'Appointment cancelled': 'Rendez-vous annulé',
          'Failed to cancel appointment': 'Échec de l\'annulation du rendez-vous',
          'Roster shift deleted successfully': 'Shift de garde supprimé avec succès',
          'Failed to delete roster shift': 'Échec de la suppression du shift de garde',
          'Roster shift saved successfully': 'Shift de garde enregistré avec succès',
          'Failed to update roster': 'Échec de la mise à jour de la garde',
          'Monday': 'Lundi',
          'Tuesday': 'Mardi',
          'Wednesday': 'Mercredi',
          'Thursday': 'Jeudi',
          'Friday': 'Vendredi',
          'Saturday': 'Samedi',
          'Sunday': 'Dimanche',
          'No contact details': 'Aucune coordonnée',
          'Unassigned': 'Non assigné',
          'mins': 'minutes',
          'scheduled': 'programmé',
          'checked_in': 'enregistré',
          'cancelled': 'annulé',
          'Day of Week': 'Jour de la semaine',
          'Start Time': 'Heure de début',
          'End Time': 'Heure de fin',
          'Edit Shift Hours': 'Modifier les heures de garde',
          'Delete Shift': 'Supprimer la garde',
          'Delete this shift rule?': 'Supprimer cette règle de garde ?',

          // Triage
          'Triage Room': 'Salle de Triage',
          'Triage Desk': 'Bureau de Triage',
          'Perform vital checks and patient categorization': 'Effectuer les contrôles vitaux et la catégorisation des patients',
          'Back to Queue': 'Retour à la File d\'Attente',
          'Warning: Critical Vital Signs Detected': 'Attention : Signes Vitaux Critiques Détectés',
          'Patient exhibits abnormal parameters (SpO2 < 92%, or abnormal HR/Temp). High ESI urgency level (Level 1 or 2) is recommended.': 'Le patient présente des paramètres anormaux (SpO2 < 92%, ou FC/Temp anormale). Un niveau d\'urgence ESI élevé (Niveau 1 ou 2) est recommandé.',
          'Body Temperature': 'Température Corporelle',
          'Normal range: 36.5°C - 37.5°C': 'Plage normale : 36.5°C - 37.5°C',
          'Heart Rate': 'Fréquence Cardiaque',
          'Normal range: 60 - 100 bpm': 'Plage normale : 60 - 100 bpm',
          'Blood Pressure (Systolic)': 'Pression Artérielle (Systolique)',
          'Normal: 90 - 120 mmHg': 'Normale : 90 - 120 mmHg',
          'Blood Pressure (Diastolic)': 'Pression Artérielle (Diastolique)',
          'Normal: 60 - 80 mmHg': 'Normale : 60 - 80 mmHg',
          'Oxygen Saturation (SpO2)': 'Saturation en Oxygène (SpO2)',
          'Normal range: 95% - 100%': 'Plage normale : 95% - 100%',
          'Triage Urgency Level': 'Niveau d\'Urgence du Triage',
          'Please select urgency level': 'Veuillez sélectionner le niveau d\'urgence',
          'Emergency (Red)': 'Urgence (Rouge)',
          'Urgent (Orange)': 'Urgent (Orange)',
          'Normal (Blue)': 'Normal (Bleu)',
          'Non-urgent (Green)': 'Non-urgent (Vert)',
          'Routine (Gray)': 'Routine (Gris)',
          'Emergency Severity Index (ESI) Triage Calculator': 'Calculateur de Triage de l\'Indice de Gravité des Urgences (ESI)',
          'Activate the decision support calculator to systematically determine the patient\'s ESI priority level.': 'Activer le calculateur d\'aide à la décision pour déterminer systématiquement le niveau de priorité ESI du patient.',
          'Does the patient require immediate life-saving intervention?': 'Le patient a-t-il besoin d\'une intervention immédiate de sauvetage ?',
          'Yes (ESI 1)': 'Oui (ESI 1)',
          'Is this a high-risk situation, confused/lethargic, or in severe pain/distress?': 'S\'agit-il d\'une situation à haut risque, d\'un patient confus/léthargique, ou en douleur/détresse sévère ?',
          'Yes (ESI 2)': 'Oui (ESI 2)',
          'How many resources is this patient predicted to need?': 'Combien de ressources ce patient devrait-il nécessiter ?',
          'None (ESI 5)': 'Aucune (ESI 5)',
          'One (ESI 4)': 'Une (ESI 4)',
          'Many (2+)': 'Plusieurs (2+)',
          'Are vitals in the danger zone? (HR > 100, RR > 20, SpO2 < 92%)': 'Les constantes sont-elles dans la zone de danger ? (FC > 100, FR > 20, SpO2 < 92%)',
          'Yes (Consider ESI 2)': 'Oui (Envisager ESI 2)',
          'No (ESI 3)': 'Non (ESI 3)',
          'Calculated ESI Level': 'Niveau ESI Calculé',
          'Resuscitation': 'Réanimation',
          'Emergent': 'Émergent',
          'Urgent': 'Urgent',
          'Less Urgent': 'Moins Urgent',
          'Non-Urgent': 'Non Urgent',
          'Save & Finalize Triage': 'Enregistrer & Finaliser le Triage',
          'Triage data saved. Patient moved to consultation queue.': 'Données de triage enregistrées. Patient déplacé vers la file d\'attente de consultation.',
          'Failed to save triage data': 'Échec de l\'enregistrement des données de triage',
          'Loading patient record...': 'Chargement du dossier patient...',
          'Age': 'Âge',
          'years': 'ans',

          // Medical History & EMR Timeline
          'Timeline': 'Chronologie',
          'Vitals Trend': 'Tendance des Constantes',
          'Medical History': 'Historique Médical',
          'No previous history': 'Aucun historique antérieur',
          'Encounter': 'Rencontre',
          'Completed': 'Terminé',
          'No historic vitals recorded': 'Aucune constante historique enregistrée',
          'BP': 'PA',
          'HR/SpO2': 'FC/SpO2',
          'Temperature (°C)': 'Température (°C)',
          'Heart Rate (bpm)': 'Fréquence Cardiaque (bpm)',
          'SpO2 (%)': 'SpO2 (%)',

          // Lab & Diagnostic Orders
          'Order Lab Tests': 'Ordonner des Examens de Laboratoire',
          'Select Tests': 'Sélectionner des Examens',
          'Please select at least one test': 'Veuillez sélectionner au moins un examen',
          'Search tests...': 'Rechercher des examens...',
          'Clinical Notes / Instructions': 'Notes Cliniques / Instructions',
          'Reason for test, special instructions...': 'Raison du test, instructions spéciales...',
          'Lab order created successfully': 'Commande de laboratoire créée avec succès',
          'Failed to create lab order': 'Échec de la création de la commande de laboratoire',

          // Pharmacy & Prescriptions
          'Write Prescription': 'Rédiger une Ordonnance',
          'Diagnosis Context': 'Contexte du Diagnostic',
          'e.g. Acute Bronchitis': 'ex. Bronchite Aiguë',
          'Medication': 'Médicament',
          'Dosage': 'Posologie',
          'Frequency': 'Fréquence',
          'Duration': 'Durée',
          'Quantity': 'Quantité',
          'Required': 'Requis',
          'Select Medication': 'Sélectionner un Médicament',
          'Add Medication': 'Ajouter un Médicament',
          'Prescription sent to pharmacy': 'Ordonnance envoyée à la pharmacie',
          'Clinical Safety Warning': 'Alerte de Sécurité Clinique',
          'Error Processing Request': 'Erreur lors du Traitement de la Demande',
          'Failed to send prescription': 'Échec de l\'envoi de l\'ordonnance',

          // Queue management
          'Triage Queue': 'File d\'Attente du Triage',
          'Consultation Queue': 'File d\'Attente des Consultations',
          'Clinical Queue': 'File d\'Attente Clinique',
          'Patients waiting for vital signs and urgency triage': 'Patients en attente de constantes et de triage d\'urgence',
          'Patients waiting for clinical consultation': 'Patients en attente de consultation clinique',
          'Real-time patient flow management': 'Gestion en temps réel du flux des patients',
          'Emergency': 'Urgence',
          'Non-urgent': 'Non urgent',
          'Routine': 'Routine',
          'Waiting': 'En attente',
          'Checked in': 'Enregistré',
          'Start Triage': 'Démarrer le Triage',
          'Edit Triage': 'Modifier le Triage',
          'Resume': 'Reprendre',
          'View': 'Voir',
          'In Triage': 'En Triage',
          'In Consultation': 'En Consultation',
          'Billed': 'Facturé',
          'Search by patient name, ID, phone...': 'Rechercher par nom de patient, ID, téléphone...',
          'Filter Urgency': 'Filtrer par Urgence',
          'All Urgency': 'Toutes les Urgences',
          'Filter Status': 'Filtrer par Statut',
          'Check In Patient': 'Enregistrer le Patient',
          'Select Patient': 'Sélectionner le Patient',
          'Search patient by name, phone...': 'Rechercher un patient par nom, téléphone...',
          'Or register a new patient first': 'Ou enregistrer d\'abord un nouveau patient',
          'Please select a patient': 'Veuillez sélectionner un patient',
          'Patient checked in successfully': 'Patient enregistré avec succès',
          'Wait Time': 'Temps d\'Attente',
          'Escalate to Emergency': 'Escalader en Urgence',
          'View Full Chart': 'Voir le Dossier Complet',
          'Refresh now': 'Actualiser maintenant',
          'Export': 'Exporter',
          'Total Today': 'Total Aujourd\'hui',
          'Filter by date range': 'Filtrer par plage de dates',
          'From date': 'Date de début',
          'To date': 'Date de fin',
          'Active filters': 'Filtres actifs',
          'Clear all': 'Effacer tout',
          'Queue': 'File d\'Attente',
          'Queue is clear for today': 'La file d\'attente est vide pour aujourd\'hui',
          'Check In First Patient': 'Enregistrer le premier patient',
          'Patient will be added to the queue with waiting status. Proceed to triage to assign urgency level.': 'Le patient sera ajouté à la file d\'attente avec le statut en attente. Procédez au triage pour attribuer le niveau d\'urgence.',
          'Updated': 'Mis à jour',
          'yrs': 'ans',
          'Net Profit': 'Bénéfice Net',
          'Gross Profit': 'Bénéfice Brut',
          'Balance': 'Solde',
          'Outgoing Amount': 'Montant Sortant',
          'Finance Report': 'Rapport Financier',
          'Export to Excel': 'Exporter vers Excel',
          'Funds Management': 'Gestion des Fonds',
          'Track your financial overview and top-ups': 'Suivez votre aperçu financier et vos recharges',
          'No data': 'Aucune donnée',
          'Debit': 'Débit',
          'Credit': 'Crédit',
          'Description': 'Description',
          'Hospital Bill': 'Facture Hôpital',
          'POS Sales': 'Ventes POS',
          'Amount Billed': 'Montant Facturé',



          // Dashboard
          'Consultations': 'Consultations',
          'Critical Patients': 'Patients Critiques',
          'Add Patient': 'Ajouter un Patient',
          'Records': 'Dossiers',
          'Wards Management': 'Gestion des Salles d\'Hospitalisation',
          'Clinical Center Overview': 'Aperçu du Centre Clinique',
          'View Queue': 'Afficher la File d\'Attente',
          'No patients in queue': 'Aucun patient dans la file d\'attente',
          'Quick Actions': 'Actions Rapides',
          'Critical Attention Required': 'Attention Critique Requise',
          'There are critical patients in the queue needing immediate attention.': 'Il y a des patients critiques dans la file d\'attente nécessitant une attention immédiate.',

          // Escrows, Finance & RCM
          'Clinical Billing & Escrow': 'Facturation Clinique & Consignation',
          'Manage patient deposits, insurance claims and doctor splits': 'Gérer les dépôts des patients, les réclamations d\'assurance et les répartitions des médecins',
          'Aggregate Escrow Funds': 'Fonds de Consignation Globaux',
          'Unsubmitted Claims': 'Réclamations non Soumises',
          'claims': 'réclamations',
          'Commissions Split Ledgers': 'Grand Livre de Répartition des Commissions',
          'My Commissions Earned': 'Mes Commissions Gagnées',
          'Patient Deposits (Escrow)': 'Dépôts Patients (Consignation)',
          'Insurance Claim Exporter': 'Exportateur de Réclamations d\'Assurance',
          'Doctor Commissions': 'Commissions des Médecins',
          'Deposit Account Workspace': 'Espace de Travail des Dépôts',
          'Search Patient': 'Rechercher un Patient',
          'Select patient to check balance...': 'Sélectionner un patient pour vérifier le solde...',
          'Escrow Balance': 'Solde de Consignation',
          'Top Up Escrow': 'Alimenter la Consignation',
          'Deduct for Bill': 'Déduire pour Facture',
          'Refund Balance': 'Rembourser le Solde',
          'No Patient Selected': 'Aucun Patient Sélectionné',
          'Search and select a patient to access their escrow account.': 'Rechercher et sélectionner un patient pour accéder à son compte de consignation.',
          'Deposit Transaction Log': 'Registre des Transactions de Dépôt',
          'Date / Time': 'Date / Heure',
          'Type': 'Type',
          'Amount': 'Montant',
          'Balance After': 'Solde Après',
          'Notes': 'Notes',
          'Operator': 'Opérateur',
          'Print Slip': 'Imprimer le Reçu',
          'No transactions logged yet.': 'Aucune transaction enregistrée pour le moment.',
          'Copay Split Claims Sheet': 'Feuille de Réclamation de Partage de Copaiement',
          'Export Insurance Claims (CSV)': 'Exporter les Réclamations d\'Assurance (CSV)',
          'Search patient or invoice...': 'Rechercher un patient ou une facture...',
          'Date': 'Date',
          'Invoice': 'Facture',
          'Insurer / Policy': 'Assureur / Police',
          'Patient Cash': 'Quote-part Patient',
          'Total': 'Total',
          'RCM Panel': 'Panneau RCM',
          'Scrub & RCM': 'Vérification & RCM',
          'Failed to log deposit': 'Échec de l\'enregistrement du dépôt',
          'Payment deducted from deposit balance': 'Paiement déduit du solde de consignation',
          'Failed to deduct deposit': 'Échec de la déduction de consignation',
          'Deposit balance refunded successfully': 'Solde de consignation remboursé avec succès',
          'Failed to process refund': 'Échec du traitement du remboursement',
          'Doctor commission splits updated': 'Répartitions des commissions des médecins mises à jour',
          'Failed to save settings': 'Échec de l\'enregistrement des paramètres',
          'Commission settled successfully': 'Commission réglée avec succès',
          'Failed to settle commission': 'Échec du règlement de la commission',
          'Claims sheet exported successfully': 'Feuille de réclamations exportée avec succès',
          'Claims export failed': 'Échec de l\'exportation des réclamations',
          'Failed to run claims scrubber': 'Échec de l\'exécution du vérificateur de réclamations',
          'Claims scrubber completed!': 'Vérificateur de réclamations terminé !',
          'EDI 837 claim format exported!': 'Format de réclamation EDI 837 exporté !',
          'Failed to export EDI 837 claim': 'Échec de l\'exportation de la réclamation EDI 837',
          'EDI 835 Remittance Processed': 'Remise EDI 835 traitée',
          'Status:': 'Statut :',
          'Approved amount:': 'Montant approuvé :',
          'Failed to process payment remittance': 'Échec du traitement de la remise de paiement',
          'copied to clipboard': 'copié dans le presse-papiers',
          'No patient data': 'Aucune donnée patient',
          'Select an encounter to view resource': 'Sélectionner une rencontre pour afficher la ressource',
          'Select a patient to view HL7/FHIR resources': 'Sélectionner un patient pour afficher les ressources HL7/FHIR',

          // Inpatient Wards & Admissions
           // Inpatient Wards & Admissions
          'Manage wards, beds, and admissions': 'Gérer les services, les lits et les admissions',
          'New Ward': 'Nouveau Service',
          'Total Wards': 'Total des Services',
          'Active Units': 'Unités Actives',
          'Bed Capacity': 'Capacité en Lits',
          'available': 'disponible',
          'Occupancy Rate': 'Taux d\'Occupation',
          'Beds offline': 'Lits hors service',
          'All operational': 'Tous opérationnels',
          'No inpatient wards configured yet. Wards must be created before adding beds.': 'Aucun service d\'hospitalisation configuré pour le moment. Les services doivent être créés avant d\'ajouter des lits.',
          'Create First Ward': 'Créer le Premier Service',
          'Add Bed': 'Ajouter un Lit',
          'Gender Policy': 'Politique de Genre',
          'Mixed': 'Mixte',
          'Admission Fee': 'Frais d\'Admission',
          'Price per Day': 'Prix par Jour',
          'Edit Ward': 'Modifier le Service',
          'Deactivate': 'Désactiver',
          'Admissions History': 'Historique des Admissions',
          'Direct Admission': 'Admission Directe',
          'Still Admitted': 'Toujours Hospitalisé',
          'Admitting Doctor': 'Médecin d\'Admission',
          'Ward / Bed': 'Salle / Lit',
          'Discharge Date': 'Date de Sortie',
          'admitted': 'Admis',
          'discharged': 'Sorti',
          'transferred': 'Transféré',
          'Admitted': 'Admis',
          'Discharged': 'Sorti',
          'Transferred': 'Transféré',
          'Admission ID': 'ID d\'Admission',
          'Inpatient Name': 'Nom du Patient',
          'Physician': 'Médecin',
          'Inpatient Ward Transfer Summary': 'Résumé de l\'Admission en Salle',
          'Admitting Ward': 'Service d\'Admission',
          'Allocated Bed': 'Lit Attribué',
          'Attending Consultant': 'Médecin Traitant',
          'Details': 'Détails',
          'Reason': 'Raison',
          'Summary': 'Résumé',
          'records': 'dossiers',
          'Create New Ward': 'Créer un Nouveau Service',
          'Ward Name': 'Nom du Service',
          'ICU': 'USI (Soins Intensifs)',
          'Maternity': 'Maternité',
          'Private': 'Privé',
          'Target Capacity': 'Capacité Cible',
          'Male Only': 'Hommes Uniquement',
          'Female Only': 'Femmes Uniquement',
          'Save Ward': 'Enregistrer le Service',
          'Ward updated successfully': 'Service mis à jour avec succès',
          'Failed to update ward': 'Échec de la mise à jour du service',
          'Deactivate Ward': 'Désactiver le Service',
          'Are you sure you want to deactivate': 'Êtes-vous sûr de vouloir désactiver',
          'Active beds and admissions will be preserved, but the ward will no longer be visible.': 'Les lits actifs et les admissions seront préservés, mais le service ne sera plus visible.',
          'Ward deactivated successfully': 'Service désactivé avec succès',
          'Deactivation failed': 'Échec de la désactivation',
          'Bed created successfully': 'Lit créé avec succès',
          'Failed to create bed': 'Échec de la création du lit',
          'Confirm Discharge': 'Confirmer la Sortie',
          'Are you sure you want to discharge this patient? This releases the bed.': 'Êtes-vous sûr de vouloir faire sortir ce patient ? Cela libérera le lit.',
          'Patient discharged': 'Patient sorti',
          'Discharge failed': 'Échec de la sortie',
          'Inpatient Clinical Workspace': 'Espace Clinique d\'Hospitalisation',
          'Close Workspace': 'Fermer l\'Espace de Travail',
          'Bed': 'Lit',
          'Gender': 'Genre',
          'Date Admitted': 'Date d\'Admission',
          'Admission Diagnosis': 'Diagnostic d\'Admission',
          'Nursing MAR & Checklist': 'MAR & Liste de Contrôle Infirmier',
          'Active Medical Orders': 'Ordonnances Médicales Actives',
          'No active medical orders found.': 'Aucune ordonnance médicale active trouvée.',
          'Log MAR Dose': 'Enregistrer une Dose MAR',
          'Are you sure you want to cancel this clinical order?': 'Êtes-vous sûr de vouloir annuler cette ordonnance clinique ?',
          'Clinical order cancelled successfully': 'Ordonnance clinique annulée avec succès',
          'Failed to cancel order': 'Échec de l\'annulation de l\'ordonnance',
          'Cancel Order': 'Annuler l\'Ordonnance',
          'Administration Log History': 'Historique d\'Administration',
          'No medication dose administrations logged yet.': 'Aucune administration de dose de médicament enregistrée pour le moment.',
          'Inpatient Vitals Charting': 'Graphique des Constantes d\'Hospitalisation',
          'Record Current Vitals': 'Saisir les Constantes Vitales',
          'Date/Time': 'Date / Heure',
          'Recorded By': 'Enregistré par',
          'BP Systolic': 'PA Systolique',
          'BP Diastolic': 'PA Diastolique',
          'Order Type': 'Type d\'Ordonnance',
          'Instructions / Directions': 'Instructions / Directives',
          'Schedule / Frequency': 'Intervalle / Fréquence',
          'e.g. Paracetamol 1g PO Q8h, Monitor O2 saturation hourly...': 'ex. Paracétamol 1g PO toutes les 8h, Surveiller la saturation en O2 toutes les heures...',
          'e.g. Q8h, 08:00, 16:00, 24:00, daily at 10 AM': 'ex. Q8h, 08:00, 16:00, 24:00, tous les jours à 10h',
          'Temperature': 'Température',
          'Save Vitals Entry': 'Enregistrer l\'Entrée des Constantes',
          'Admission Vitals History Trends': 'Tendances de l\'Historique des Constantes d\'Admission',
          'Not enough vital logs recorded for this admission to show chart trends.': 'Pas assez d\'enregistrements de constantes pour cette admission pour afficher les graphiques de tendances.',
          'Vitals Record History Logs': 'Registres d\'Historique des Constantes',
          'No vitals records logged yet.': 'Aucune constante enregistrée pour le moment.',
          'Triage Nurse': 'Infirmier(ère) de Triage',
          'Clinical Timeline': 'Chronologie Clinique',
          'EHR File': 'Dossier EHR',
          'Prescription History': 'Historique des Ordonnances',
          'Laboratory Results': 'Résultats de Laboratoire',
          'Loading timeline...': 'Chargement de la chronologie...',
          'No clinical encounters found': 'Aucune visite clinique trouvée',
          'Loading patient profile...': 'Chargement du profil du patient...',
          'Back to Patients': 'Retour aux Patients',
          'Edit Patient Profile': 'Modifier le Profil du Patient',
          'Physician Order Panel (CPOE)': 'Panneau de Prescription du Médecin (CPOE)',
          'Prescribe / Issue New Order': 'Prescrire / Émettre une Nouvelle Ordonnance',
          'Monitoring & Observation': 'Surveillance & Observation',
          'Specialized Diet': 'Régime Spécialisé',
          'Medical Procedure': 'Procédure Médicale',
          'Clinical Instructions': 'Instructions Cliniques',
          'Scheduled Interval / Times (Optional)': 'Intervalle Programmé / Heures (Optionnel)',
          'Issue Medical Order': 'Émettre l\'Ordonnance Médicale',
          'All Issued Medical Orders': 'Toutes les Ordonnances Médicales Émises',
          'No medical orders issued yet.': 'Aucune ordonnance médicale émise pour le moment.',
          'Log Medication/Instructions Dose': 'Enregistrer la Dose de Médicament/Instruction',
          'Confirm Entry': 'Confirmer la Saisie',
          'Administration Outcome': 'Résultat de l\'Administration',
          'Given': 'Administré',
          'Missed': 'Manqué',
          'Refused': 'Refusé',
          'Outcome Notes / Remarks': 'Notes / Remarques sur le Résultat',
          'e.g. Patient swallowed tablet successfully; Refused due to nausea...': 'Ex: Le patient a bien avalé le comprimé; Refusé en raison de nausées...',
          'Schedule': 'Classification',
          'Confirm Admission': 'Confirmer l\'Admission',
          'Assigning patient to ward and bed location.': 'Assignation du patient au service et au lit.',
          'Target Bed': 'Lit Cible',
          'Encounter ID (Optional)': 'ID de Rencontre (Optionnel)',
          'Link this admission directly to a patient consultation encounter': 'Lier directement cette admission à une rencontre de consultation de patient',
          'Direct Admission (No Encounter)': 'Admission Directe (Aucune Rencontre)',
          'None (Direct Admission)': 'Aucune (Admission Directe)',
          'mixed': 'mixte',
          'male': 'homme',
          'female': 'femme',
          'Occupied': 'Occupé',
          'Available': 'Disponible',
          'Release Maintenance': 'Libérer de Maintenance',
          'Mark Maintenance': 'Marquer en Maintenance',
          'Under Maintenance': 'En Maintenance',
          'Empty Bed': 'Lit Vide',
          'Chart': 'Dossier',
          'Discharge': 'Sortie',
          'Release': 'Libérer',

          // PACS Medical Imaging
          'PACS Medical Imaging': 'Imagerie Médicale PACS',
          'No imaging study loaded': 'Aucun examen d\'imagerie chargé',
          'No studies found. Upload a scan using the import panel on the left.': 'Aucun examen trouvé. Importez un scanner depuis le panneau de gauche.',
          'Search for a patient above to begin.': 'Recherchez un patient ci-dessus pour commencer.',
          'Study Browser': 'Explorateur d\'Examens',
          'Loading studies…': 'Chargement des examens…',
          'Select a patient from the search bar to browse imaging studies': 'Sélectionner un patient dans la barre de recherche pour parcourir ses examens',
          'No imaging studies found. Upload a scan below.': 'Aucun examen d\'imagerie trouvé. Importez un scanner ci-dessous.',
          'Import Scan': 'Importer un Scanner',
          'Modality / Device': 'Modalité / Appareil',
          'Study Label': 'Étiquette de l\'Examen',
          'optional': 'optionnel',
          'Initial Finding Notes': 'Notes Cliniques Initiales',
          'Enter visible clinical findings…': 'Saisir les résultats cliniques visibles…',
          'DICOM / Image File': 'Fichier DICOM / Image',
          'Click or drop .dcm / PNG / JPG': 'Cliquez ou déposez .dcm / PNG / JPG',
          'Upload to Patient File': 'Téléverser dans le Dossier Patient',
          'Measure': 'Mesurer',
          'Invert': 'Inverser',
          'Zoom In': 'Zoomer',
          'Zoom Out': 'Dézoomer',
          'Rotate 90°': 'Faire pivoter de 90°',
          'Reset': 'Réinitialiser',
          'Zoom': 'Zoom',
          'Brightness (WL)': 'Luminosité (WL)',
          'Contrast (WW)': 'Contraste (WW)',
          'Findings': 'Résultats',
          'DICOM Tags': 'Tags DICOM',
          'FHIR Export': 'Export FHIR',
          'Diagnostic Findings': 'Observations Diagnostiques',
          'Describe radiology findings and clinical observations in detail here...': 'Décrire en détail les observations radiologiques et cliniques ici...',
          'Save Clinical Report': 'Enregistrer le Rapport Clinique',
          'Scan Details': 'Détails du Scanner',
          'DICOM Header Tags': 'Tags d\'En-tête DICOM',
          'Select a study to view DICOM tags': 'Sélectionner un examen pour afficher les tags DICOM',
           // Backend Error details

          // Backend Error details
          'Commission log not found': 'Registre de commission non trouvé',
          'Commission is already paid': 'La commission est déjà payée',
          'Invalid Encounter ID. The encounter does not exist or does not belong to this patient.': 'ID de rencontre invalide. La rencontre n\'existe pas ou n\'appartient pas à ce patient.',
          'Scheduled start time must be before end time': 'L\'heure de début programmée doit être antérieure à l\'heure de fin',
          'Scheduling conflict: Operating Room is already booked during this time range': 'Conflit de planification : La salle d\'opération est déjà réservée pendant cette période',
          'Scheduling conflict: Surgeon is already scheduled for another surgery during this time range': 'Conflit de planification : Le chirurgien est déjà programmé pour une autre chirurgie pendant cette période',
          'Scheduling conflict: Anesthetist is already scheduled for another surgery during this time range': 'Conflit de planification : L\'anesthésiste est déjà programmé pour une autre chirurgie pendant cette période',
          'Surgical schedule not found': 'Programme chirurgical non trouvé',
          'WHO checklist not found for this schedule': 'Liste de contrôle de l\'OMS non trouvée pour ce programme',
          'Anesthesia record not found for this schedule': 'Dossier d\'anesthésie non trouvé pour ce programme',
          'Invalid HL7 message: Accession ID (OBR-3/OBR-2) is missing': 'Message HL7 invalide : L\'identifiant d\'accès (OBR-3/OBR-2) est manquant',
          'Patient not found or does not belong to this shop': 'Patient non trouvé ou n\'appartient pas à cette boutique',
          'Physician not found or does not belong to this shop': 'Médecin non trouvé ou n\'appartient pas à cette boutique',
          'Appointment not found': 'Rendez-vous non trouvé',
          'Roster shift not found': 'Shift de roulement non trouvé',
          'Addendums are only for locked encounters. Use consultation update instead.': 'Les addendas ne sont valables que pour les rencontres verrouillées. Utilisez plutôt la mise à jour de la consultation.',
          'Cannot add diagnosis to a locked encounter': 'Impossible d\'ajouter un diagnostic à une rencontre verrouillée',
          'Failed to save clinical report': 'Échec de l\'enregistrement du rapport clinique',
          'Findings saved': 'Observations enregistrées',
          'Failed to save findings': 'Échec de l\'enregistrement des observations',
          'Attachment deleted': 'Pièce jointe supprimée',
          'Delete failed': 'Échec de la suppression',
          'Select a file to upload': 'Sélectionnez un fichier à charger',
          'Upload failed': 'Échec du chargement',
          'Select a patient first': 'Sélectionnez d\'abord un patient',
          // Consultation Room
          'Clinical workspace and EMR documentation': 'Espace de travail clinique et documentation du DME',
          'View Invoice': 'Voir la facture',
          'Mark VIP': 'Marquer VIP',
          'VIP Chart Unlocked': 'Dossier VIP déverrouillé',
          'This is a restricted VIP patient chart. Your access has been logged under HIPAA compliance regulations. All actions are actively audited.': 'Il s\'agit d\'un dossier patient VIP restreint. Votre accès a été enregistré conformément aux réglementations de conformité HIPAA. Toutes les actions sont activement auditées.',
          'Medical Record Locked': 'Dossier médical verrouillé',
          'This clinical note has been finalized and is now immutable. Addendums must be used for any corrections.': 'Cette note clinique a été finalisée et est désormais immuable. Des addendas doivent être utilisés pour toute correction.',
          'Patient ID': 'ID Patient',
          'Triage Summary': 'Résumé du triage',
          'Blood Pressure': 'Pression Artérielle',
          'Diagnoses': 'Diagnostics',
          'No diagnosis recorded': 'Aucun diagnostic enregistré',
          'Subjective (S)': 'Subjectif (S)',
          'Objective (O)': 'Objectif (O)',
          'Assessment (A)': 'Évaluation (A)',
          'Plan (P)': 'Plan (P)',
          'Subjective': 'Subjectif',
          'Objective': 'Objectif',
          'Assessment': 'Évaluation',
          'Plan': 'Plan',
          'Specialty': 'Spécialité',
          'Labs & Rx': 'Analyses & Rx',
          'Orders & Rx': 'Prescriptions & Soins',
          'Chief complaint, history of present illness, history records...': 'Plainte principale, historique de la maladie actuelle, antécédents...',
          'Physical examination findings, clinical observations and notes...': 'Résultats de l\'examen physique, observations et notes cliniques...',
          'Clinical assessment and differential diagnoses...': 'Évaluation clinique et diagnostics différentiels...',
          'Treatment plan, patient advice and follow-up clinical actions...': 'Plan de traitement, conseils au patient et actions cliniques de suivi...',
          'Lab Orders': 'Examens de laboratoire',
          'No lab tests ordered': 'Aucun examen de laboratoire prescrit',
          'No prescriptions': 'Aucune ordonnance',
          'Save Progress': 'Enregistrer les modifications',
          'Addendums': 'Addendas',
          'Add Correction': 'Ajouter une correction',
          'No addendums recorded': 'Aucun addenda enregistré',
          'Correction': 'Correction',
          'Consultation Fee': 'Frais de consultation',
          'Consultation Fee Amount': 'Montant des frais de consultation',
          'Please input billing amount': 'Veuillez entrer le montant de la facturation',
          'Select Currency': 'Sélectionner la devise',
          'Please select currency': 'Veuillez sélectionner la devise',
          'Generate Order': 'Générer la commande',
          'Add Diagnosis': 'Ajouter un diagnostic',
          'ICD Code': 'Code CIM',
          'Please enter ICD code': 'Veuillez entrer le code CIM',
          'Please enter diagnosis description': 'Veuillez entrer la description du diagnostic',
          'Add Clinical Note Correction': 'Ajouter une correction de note clinique',
          'Correction Note (Addendum)': 'Note de correction (Addenda)',
          'Please enter correction details': 'Veuillez entrer les détails de la correction',
          'Provide details about any clinical corrections or addendums...': 'Fournir des détails sur les corrections cliniques ou les addendas...',
          'Save Correction': 'Enregistrer la correction',
          // Triage / Patient Queue
          'Urgency': 'Urgence',
          'Vitals': 'Constantes',
          // Patients List
          'Manage patient records': 'Gérer les dossiers patients',
          'With Allergies': 'Avec allergies',
          'Search by name, phone, or email...': 'Rechercher par nom, téléphone ou email...',
          'No insurance': 'Pas d\'assurance',
          'Check In': 'Enregistrer',
          'patients': 'patients',
          'years old': 'ans',
          'Cancel': 'Annuler',
          'New This Month': 'Nouveau ce mois-ci',
          
          // Specialty templates
          'Specialty Ward Template': 'Modèle de Service de Spécialité',
          'No Specialty Template': 'Aucun Modèle de Spécialité',
          'Pediatrics Ward': 'Service de Pédiatrie',
          'OB-GYN / Maternity': 'OB-GYN / Maternité',
          'Cardiology Clinic': 'Clinique de Cardiologie',
          'Select a specialized template to capture structured medical metrics for this encounter.': 'Sélectionnez un modèle spécialisé pour enregistrer les mesures médicales structurées de cette rencontre.',
          'Antenatal Care & Obstetric History': 'Soins Prénatals & Historique Obstétrique',
          'Last Menstrual Period (LMP)': 'Date des Dernières Règles (DDR)',
          'LMP date is required': 'La date de la DDR est requise',
          'Gestational Age (GA)': 'Âge Gestationnel (AG)',
          'Estimated Date of Delivery (EDD)': 'Date Prévue d\'Accouchement (DPA)',
          'Obstetric History (GPAL)': 'Historique Obstétrique (GPAL)',
          'Gravida (G)': 'Gravide (G)',
          'Para (P)': 'Para (P)',
          'Abortions (A)': 'Avortements (A)',
          'Living (L)': 'Enfants Vivants (V)',
          'Active Pregnancy Monitoring': 'Surveillance Active de la Grossesse',
          'Fundal Height (cm)': 'Hauteur Utérine (cm)',
          'Fetal Heart Rate (bpm)': 'Rythme Cardiaque Fœtal (bpm)',
          'Presentation': 'Présentation',
          'Select presentation': 'Sélectionner la présentation',
          'Cephalic': 'Céphalique',
          'Breech': 'Siège',
          'Transverse': 'Transverse',
          'Pediatric Growth & Developmental Milestones': 'Croissance Pédiatrique & Étapes du Développement',
          'Birth Weight (kg)': 'Poids de Naissance (kg)',
          'Birth Height (cm)': 'Taille de Naissance (cm)',
          'Current Weight (kg)': 'Poids Actuel (kg)',
          'Current Height (cm)': 'Taille Actuelle (cm)',
          'Head Circumference (cm)': 'Périmètre Crânien (cm)',
          'Developmental Milestones': 'Étapes du Développement',
          'Social Smile (2 Months)': 'Sourire Social (2 Mois)',
          'Holds Head Steady (4 Months)': 'Maintien de la Tête Stable (4 Mois)',
          'Rolls Over (6 Months)': 'Se Retourne (6 Mois)',
          'Sits Without Support (8 Months)': 'S\'assoit Sans Support (8 Mois)',
          'Stands Steady (11 Months)': 'Tient Debout Stable (11 Mois)',
          'Walks Independently (12 Months)': 'Marche Indépendamment (12 Mois)',
          'Cardiovascular Evaluation & ASCVD Risk': 'Évaluation Cardiovasculaire & Risque ASCVD',
          'Cardiac Symptoms & Symptoms Log': 'Symptômes Cardiaques & Journal des Symptômes',
          'Chest Pain': 'Douleur Thoracique',
          'Palpitations': 'Palpitations',
          'Shortness of Breath': 'Essoufflement (Dyspnée)',
          'Dizziness/Syncope': 'Étourdissements/Syncope',
          'Heart Sound': 'Bruits Cardiaques',
          'Select heart sound': 'Sélectionner le bruit cardiaque',
          'Normal S1/S2': 'Normal S1/S2',
          'S3 Gallop': 'Galop S3',
          'S4 Gallop': 'Galop S4',
          'Systolic Murmur': 'Souffle Systolique',
          'Diastolic Murmur': 'Souffle Diastolique',
          'ECG Rhythm': 'Rythme ECG',
          'Select ECG rhythm': 'Sélectionner le rythme ECG',
          'Normal Sinus': 'Sinusal Normal',
          'Atrial Fibrillation': 'Fibrillation Auriculaire',
          'Sinus Tachycardia': 'Tachycardie Sinusale',
          'Sinus Bradycardia': 'Bradycardie Sinusale',
          'Ventricular Arrhythmia': 'Arythmie Ventriculaire',
          'ASCVD Risk Score Factors': 'Facteurs du Score de Risque ASCVD',
          'Systolic BP (mmHg)': 'PA Systolique (mmHg)',
          'Diastolic BP (mmHg)': 'PA Diastolique (mmHg)',
          'Total Cholesterol (mg/dL)': 'Cholestérol Total (mg/dL)',
          'HDL Cholesterol (mg/dL)': 'Cholestérol HDL (mg/dL)',
          'Active Smoker': 'Tabagisme Actif',
          'Diabetic Patient': 'Patient Diabétique',
          'Estimated 10-Year ASCVD Risk': 'Risque ASCVD à 10 ans estimé',
          'High Risk': 'Risque Élevé',
          'Intermediate Risk': 'Risque Intermédiaire',
          'Low Risk': 'Risque Faible',
          'Save Specialty Template': 'Enregistrer le Modèle de Spécialité',
          'Failed to load specialty template data': 'Échec du chargement des données du modèle de spécialité',
          'Specialty template data saved successfully': 'Données du modèle de spécialité enregistrées avec succès',
          'Failed to save specialty data': 'Échec de l\'enregistrement des données de spécialité',
          'weeks': 'semaines',
          'days': 'jours',
          // Hospital settings and ledger translations
          'Configure consultation fees, clinician availability shifts, and global HMS settings': 'Configurer les frais de consultation, les gardes de disponibilité des cliniciens et les paramètres globaux du HMS',
          'Consultation Fees': 'Frais de consultation',
          'Doctor Roster Shifts': 'Planification des gardes des médecins',
          'Multi-Tenant Fee Configuration': 'Configuration des frais multi-locataires',
          'Consultation fees are specific to your hospital. Each hospital on the platform manages its own fee schedule, fully isolated from other tenants.': 'Les frais de consultation sont spécifiques à votre hôpital. Chaque hôpital sur la plateforme gère son propre barème de frais, entièrement isolé des autres locataires.',
          'Consultation Fee Schedule': 'Grille des tarifs de consultation',
          'Configure pricing structures suggested automatically on patient billing modal': 'Configurer les structures de tarification suggérées automatiquement sur le modal de facturation des patients',
          'Add Fee Rule': 'Ajouter une règle de tarification',
          'No consultation fees configured yet.': 'Aucun frais de consultation configuré pour le moment.',
          'Add First Fee Rule': 'Ajouter la première règle de tarification',
          'Not set': 'Non défini',
          'Practitioner Shifts & Availability': 'Gardes et disponibilité des praticiens',
          'Configure standard weekly operational slots for clinical staff. System validates these when scheduling outpatient appointments.': 'Configurez les créneaux opérationnels hebdomadaires standard pour le personnel clinique. Le système les valide lors de la planification des rendez-vous externes.',
          'Select Practitioner': 'Sélectionner un praticien',
          'Search doctor or nurse...': 'Rechercher un médecin ou un infirmier...',
          'Select a consulting practitioner from the dropdown to manage their shifts.': 'Sélectionnez un praticien consultant dans la liste déroulante pour gérer ses gardes.',
          'Weekly Shift Calendar': 'Calendrier hebdomadaire des gardes',
          'Add Shift Slot': 'Ajouter un créneau de garde',
          'Select a practitioner to load active roster shifts.': 'Sélectionnez un praticien pour charger les gardes actives.',
          'No weekly shift logs for this practitioner.': 'Aucun journal de garde hebdomadaire pour ce praticien.',
          'Add First Shift': 'Ajouter une première garde',
          'Add Consultation Fee Rule': 'Ajouter une règle de frais de consultation',
          'Configure the consultation fee for a specific urgency level. Existing configs for that level will be overwritten.': 'Configurez les frais de consultation pour un niveau d\'urgence spécifique. Les configurations existantes pour ce niveau seront écrasées.',
          'Urgency Level': 'Niveau d\'urgence',
          'Select level...': 'Sélectionner le niveau...',
          'Label': 'Libellé',
          'Please enter a label': 'Veuillez saisir un libellé',
          'e.g. Standard OPD Consultation': 'ex. Consultation externe standard',
          'Fee Amount': 'Montant des frais',
          'Please enter the fee amount': 'Veuillez saisir le montant des frais',
          'Save Fee': 'Enregistrer le tarif',
          'Add Weekly Shift Slot': 'Ajouter un créneau de garde hebdomadaire',
          'Please select day of the week': 'Veuillez sélectionner le jour de la semaine',
          'Please select start time': 'Veuillez sélectionner l\'heure de début',
          'Please select end time': 'Veuillez sélectionner l\'heure de fin',
          'Save Shift': 'Enregistrer la garde',
          'Delete Fee Configuration?': 'Supprimer la configuration des frais ?',
          'Are you sure you want to delete the': 'Êtes-vous sûr de vouloir supprimer la',
          'fee? This cannot be undone.': 'frais ? Cette action est irréversible.',
          'Failed to delete': 'Échec de la suppression',
          'Fee configuration deleted': 'Configuration des frais supprimée',
          'Consultation fee updated': 'Frais de consultation mis à jour',
          'Failed to update fee': 'Échec de la mise à jour des frais',
          'Consultation fee saved successfully': 'Frais de consultation enregistrés avec succès',
          'Failed to save fee': 'Échec de l\'enregistrement des frais',
          'Doctor roster shift updated successfully': 'Garde de planification du médecin mise à jour avec succès',
          'Failed to save roster': 'Échec de l\'enregistrement de la garde',
          'Delete Roster Shift?': 'Supprimer le créneau de garde ?',
          'Are you sure you want to delete the availability shift for': 'Êtes-vous sûr de vouloir supprimer le créneau de disponibilité pour',
          'Roster shift deleted': 'Créneau de garde supprimé',
          'Failed to delete shift': 'Échec de la suppression du créneau de garde',
          'Minimal': 'Minimal',
          'Non-urgent, routine visits': 'Visites de routine, non urgentes',
          'Minor complaints, low priority': 'Plaintes mineures, faible priorité',
          'Standard': 'Standard',
          'Regular outpatient consultation': 'Consultation externe régulière',
          'Urgent care needed, same-day': 'Soins urgents requis, le jour même',
          'Life-threatening, immediate care': 'Pronostic vital engagé, soins immédiats',
          'Revenue Share Split (Cash vs Insurance)': 'Répartition du partage des revenus (Espèces vs Assurance)',
          'Cash / Copay': 'Espèces / Copaiement',
          'Insurance': 'Assurance',
          'Payers Shares': 'Parts des tiers payeurs',
          'Cash Patient Copays': 'Copaiements des patients en espèces',
          'Search invoice ID, client name, payer...': 'Rechercher un ID de facture, un nom de client, un payeur...',
          'Payer Mode': 'Mode de paiement',
          'Cash Only': 'Espèces uniquement',
          'Insurance Only': 'Assurance uniquement',
          'Copay Split': 'Partage copaiement',
          'All Invoices': 'Toutes les factures',
          'Paid': 'Payé',
          'Patient Name': 'Nom du patient',
          'Billing Type': 'Type de facturation',
          'Insurance Plan': 'Régime d\'assurance',
          'Total Billed': 'Total facturé',
          'Action': 'Action',
          'No dynamic payer distribution records': 'Aucun enregistrement dynamique de la répartition des payeurs',
          'No dynamic revenue trend data recorded': 'Aucune donnée de tendance dynamique des revenus enregistrée',
          'Claims submitted and cash collections complete.': 'Réclamations soumises et encaissements terminés.',
          'Outstanding Insurance Claims': 'Créances d\'assurance en attente',
          'Pending verification audits by insurance desks.': 'Audits de vérification en attente par les services d\'assurance.',
          'Export Ledger': 'Exporter le grand livre',
          'Invoice ID': 'ID Facture',
          'Billing & Subscription': 'Facturation & Abonnement',
          'Manage your workspace license and payment history.': 'Gérez la licence de votre espace de travail et l\'historique des paiements.',
          'Current Plan': 'Plan Actuel',
          'Plan Pricing': 'Tarif du Plan',
          'Renewal Expiry Date': 'Date d\'Expiration du Renouvellement',
          'Subscription Code': 'Code d\'Abonnement',
          'Renew Subscription': 'Renouveler l\'Abonnement',
          'ACTIVE': 'ACTIF',
          'EXPIRING SOON': 'EXPIRE BIENTÔT',
          'GRACE PERIOD': 'PÉRIODE DE GRÂCE',
          'EXPIRED': 'EXPIRÉ',
          'LICENSE BLOCKED': 'LICENCE BLOQUÉE',
          'LICENSE SUSPENDED': 'LICENCE SUSPENDUE',
          'Renew Your Service Subscription': 'Renouveler votre Abonnement au Service',
          'Your platform workspace license has expired. Complete your subscription payment to restore access to all databases, POS interfaces, inventory, and clinical logs instantly.': 'Votre licence d\'espace de travail de plateforme a expiré. Effectuez le paiement de votre abonnement pour rétablir instantanément l\'accès à toutes les bases de données, interfaces POS, inventaires et journaux cliniques.',
          'Zero Data Loss Guarantee': 'Garantie de Zéro Perte de Données',
          'All items, history logs, and billing invoices are securely preserved during expiration.': 'Tous les articles, journaux d\'historique et factures de facturation sont conservés en toute sécurité pendant l\'expiration.',
          'Instant Synchronization': 'Synchronisation Instantanée',
          'Flutterwave callbacks automatically sync your license status in real-time.': 'Les rappels de Flutterwave synchronisent automatiquement le statut de votre licence en temps réel.',
          'License Profile': 'Profil de Licence',
          'CHOOSE EXTENSION TERM': 'CHOISIR LA DURÉE DE PROLONGATION',
          '1 Month (Standard Plan)': '1 Mois (Plan Standard)',
          '3 Months (Quarterly Saver)': '3 Mois (Économie Trimestrielle)',
          '6 Months (Semi-Annual Saver)': '6 Mois (Économie Semestrielle)',
          '12 Months (Annual Premium)': '12 Mois (Premium Annuel)',
          'Base Monthly Rate': 'Tarif Mensuel de Base',
          'Billing Period': 'Période de Facturation',
          'Amount Due (USD)': 'Montant Dû (USD)',
          'Secure Checkout': 'Paiement Sécurisé',
          'Payments are processed securely via Flutterwave. Cards, mobile money, and local banks are supported.': 'Les paiements sont traités en toute sécurité via Flutterwave. Les cartes, le paiement mobile et les banques locales sont pris en charge.',
          'Proceed to Secure Payment': 'Procéder au Paiement Sécurisé',
          'Month(s)': 'Mois',
          'Month': 'Mois',
          'N/A': 'N/A',
          'Pay with Cash': 'Payer en Espèces',
          'Online Payment (Card, MoMo)': 'Paiement en ligne (Carte, MoMo)',
          'PAYMENT METHOD': 'MODE DE PAIEMENT',
          'Cash Payment Instructions': 'Instructions pour le Paiement en Espèces',
          'Acknowledge Cash Payment': 'Confirmer le Paiement en Espèces',
          'Cash renewal request registered! Please contact support to complete payment.': 'Demande de renouvellement en espèces enregistrée ! Veuillez contacter le support pour finaliser le paiement.',
          'Only the owner or administrator of this business can renew the subscription.': 'Seul le propriétaire ou l\'administrateur de cette entreprise peut renouveler l\'abonnement.',
          'Please contact your manager or business administrator to renew the license.': 'Veuillez contacter votre responsable ou administrateur système pour renouveler la licence.',
          'Administrator Contact Details:': 'Coordonnées de l\'Administrateur :',

          // ─── Hardware Store & Contractors Translations ────────────────────────
          'Contractors & Trade Customers': 'Entrepreneurs & Clients Professionnels',
          'Hardware Spool & Stock Catalog': 'Catalogue de Tourets & Stocks Quincaillerie',
          'Dynamic inventory catalog, spool stock levels, profit margin calculation, and custom categories': 'Catalogue d\'inventaire dynamique, niveaux de tourets, calcul des marges et catégories personnalisées',
          'Add Hardware Item': 'Ajouter un Article Quincaillerie',
          'Add Hardware Catalog Item': 'Ajouter un Article au Catalogue Quincaillerie',
          'Register a new SKU, wire spool stock, or building material to inventory': 'Enregistrer un nouveau SKU, touret de fil ou matériau de construction dans l\'inventaire',
          'Item Identification & Category': 'Identification de l\'Article & Catégorie',
          'ITEM IDENTIFICATION & CATEGORY': 'IDENTIFICATION DE L\'ARTICLE & CATÉGORIE',
          'Part # / SKU Code': 'N° de Pièce / Code SKU',
          'PART # / SKU CODE': 'N° DE PIÈCE / CODE SKU',
          'Part # / SKU is required': 'Le n° de pièce / SKU est requis',
          'Item Name': 'Nom de l\'Article',
          'ITEM NAME': 'NOM DE L\'ARTICLE',
          'Item name is required': 'Le nom de l\'article est requis',
          'Unit of Measure (UOM)': 'Unité de Mesure (UDM)',
          'UNIT OF MEASURE (UOM)': 'UNITÉ DE MESURE (UDM)',
          'Stock Type & Inventory Control': 'Type de Stock & Contrôle d\'Inventaire',
          'STOCK TYPE & INVENTORY CONTROL': 'TYPE DE STOCK & CONTRÔLE D\'INVENTAIRE',
          'Inventory Stock Type': 'Type de Stock d\'Inventaire',
          'INVENTORY STOCK TYPE': 'TYPE DE STOCK D\'INVENTAIRE',
          'Standard Discrete Unit Stock': 'Stock Unitaire Standard',
          '📦 Standard Discrete Unit Stock': '📦 Stock Unitaire Standard',
          'Cut-to-Length Wire / Spool Stock': 'Stock de Touret / Fil de Coupe',
          '✂️ Cut-to-Length Cable / Hose Spool': '✂️ Touret de Câble / Tuyau de Coupe',
          'Initial Stock Qty / Spool Length': 'Quantité de Stock Initiale / Longueur du Touret',
          'INITIAL STOCK QTY / SPOOL LENGTH': 'QUANTITÉ DE STOCK INITIALE / LONGUEUR DU TOURET',
          'Pricing & Financial Margins': 'Prix & Marges Financières',
          'PRICING & FINANCIAL MARGINS': 'PRIX & MARGES FINANCIÈRES',
          'Cost Price per Unit': 'Prix d\'Achat Unitaire',
          'COST PRICE PER UNIT': 'PRIX D\'ACHAT UNITAIRE',
          'Selling / Retail Price': 'Prix de Vente / Détail',
          'SELLING / RETAIL PRICE': 'PRIX DE VENTE / DÉTAIL',
          'Save Hardware Item': 'Enregistrer l\'Article Quincaillerie',
          'Meter (m)': 'Mètre (m)',
          'Kilogram (kg)': 'Kilogramme (kg)',
          'Piece / Unit (pcs)': 'Pièce / Unité (pcs)',
          'Unit / Piece (pcs)': 'Unité / Pièce (pcs)',
          'Bag / Sack (50kg)': 'Sac (50kg)',
          'Box / Carton (box)': 'Boîte / Carton (boîte)',
          'Roll / Spool (roll)': 'Rouleau / Touret (rouleau)',
          'Manage Business Categories': 'Gérer les Catégories d\'Activité',
          'Organize hardware stock, spools, machinery, and building materials into dynamic categories': 'Organiser le stock de quincaillerie, tourets, machines et matériaux en catégories dynamiques',
          'Create New Category': 'Créer une Nouvelle Catégorie',
          'Category Description': 'Description de la Catégorie',
          'Create Category': 'Créer la Catégorie',
          'Update Category': 'Mettre à jour la Catégorie',
          'Category Directory': 'Répertoire des Catégories',
          'CATEGORY DIRECTORY': 'RÉPERTOIRE DES CATÉGORIES',
          'Search categories...': 'Rechercher des catégories...',
          'Custom': 'Personnalisée',
          'System Standard': 'Standard Système',
          'Hose Spool': 'Touret de Tuyau',
          'Brass fittings': 'Raccords en laiton',
          'Electrical Cables & Wiring': 'Câbles Électriques & Câblage',
          'Power cables, wire spools, conduits, switches ...': 'Câbles d\'alimentation, tourets de fil, conduits, interrupteurs ...',
          'Plumbing, Piping & Fittings': 'Plomberie, Tuyauterie & Raccords',
          'PVC pipes, copper tubing, brass valves, coupli...': 'Tuyaux PVC, tubes en cuivre, vanne laiton, raccordement ...',
          'Power Tools & Heavy Machinery': 'Outillage Électroportatif & Machines Lourdes',
          'Drills, saws, grinders, compressors & generato...': 'Perceuses, meuleuses, compresseurs & générateurs ...',
          'Masonry, Cement & Building Supplies': 'Maçonnerie, Ciment & Matériaux de Construction',
          'Portland cement, sand, gravel, rebar, bricks & ...': 'Ciment Portland, sable, gravier, barres de fer, briques & ...',
          'TOTAL HARDWARE SKUS': 'TOTAL SKUS QUINCAILLERIE',
          'Total Hardware SKUs': 'Total SKUs Quincaillerie',
          'CUT-TO-LENGTH SPOOLS': 'TOURETS DE COUPE À LA LONGUEUR',
          'Cut-to-Length Spools': 'Tourets de Coupe à la Longueur',
          'TOTAL STOCK ASSET VALUATION': 'VALEUR TOTALE DU STOCK',
          'Total Stock Asset Valuation': 'Valeur Totale du Stock',
          'Inventory Asset Value': 'Valeur du Stock d\'Inventaire',
          'INVENTORY ASSET VALUE': 'VALEUR DU STOCK D\'INVENTAIRE',
          'ITEM & CATEGORY': 'ARTICLE & CATÉGORIE',
          'Item & Category': 'Article & Catégorie',
          'PRICING & MARGIN': 'PRIX & MARGE',
          'Pricing & Margin': 'Prix & Marge',
          'POS Sales Transactions History': 'Historique des Ventes POS',
          'Vente POSs Transactions History': 'Historique des Ventes POS',
          'Category Sales Revenue Distribution': 'Répartition des Ventes par Catégorie',
          'METER': 'MÈTRE',
          'UNIT': 'UNITÉ',
          'Search hardware items by SKU, name, category...': 'Rechercher par SKU, nom, catégorie...',
          'Search hardware items by SKU, name,...': 'Rechercher par SKU, nom...',
          'Export CSV Report': 'Exporter le Rapport CSV',
          'All Catalog Inventory & Asset Valuation': 'Tout l\'Inventaire & Valorisation des Stocks',
          'Cut-to-Length Cable & Pipe Spools': 'Tourets de Câble & Tuyau de Coupe',
          'Asset Valuation & Rate': 'Valorisation du Stock & Tarif',
          'ASSET VALUATION & RATE': 'VALORISATION DU STOCK & TARIF',
          'Spool Value & Rate': 'Valeur du Touret & Tarif',
          'Rate': 'Tarif',
          'Rate:': 'Tarif:',
          'Cable & Spool Cut-to-Length Calculator': 'Calculateur de Coupe de Câble & Touret',
          'Current Spool Stock': 'Stock Actuel du Touret',
          'Remaining After Cut': 'Reste Après Coupe',
          'Spool Capacity Remaining': 'Capacité Restante du Touret',
          'Requested Cut Length': 'Longueur de Coupe Demandée',
          'Unit Rate': 'Tarif Unitaire',
          'Optional Scrap / Waste Length': 'Chute / Déchet Optionnel',
          'Calculated Cut Price': 'Prix de Coupe Calculé',
          'Cut & Add to Order': 'Couper & Ajouter à la Commande',
          'Electrical Cable': 'Câble Électrique',
          'Electrical': 'Électrique',
          'Hardware Store': 'Quincaillerie -',
          'Executive Dashboard': 'Tableau de Bord Exécutif',
          'Real-time operational metrics, cable spool stock, tool rentals, and contractor credit tracking': 'Métriques opérationnelles en temps réel, stock de tourets, locations d\'outils et suivi des crédits entrepreneurs',
          'Manage Equipment Rentals': 'Gérer les Locations d\'Équipements',
          'ACTIVE SPOOLS COUNT': 'NOMBRE DE TOURETS ACTIFS',
          'Active Spools Count': 'Nombre de Tourets Actifs',
          'ACTIVE TOOL RENTALS': 'LOCATIONS D\'OUTILS ACTIVES',
          'Active Tool Rentals': 'Locations d\'Outils Actives',
          'HARDWARE OPERATIONS COMMAND': 'COMMANDE DES OPÉRATIONS DE QUINCAILLERIE',
          'Hardware Operations Command': 'Commande des Opérations de Quincaillerie',
          'Hardware POS': 'Caisse POS Quincaillerie',
          'Contractors & Debts': 'Entrepreneurs & Dettes',
          'Recent Operational Audit Stream': 'Flux d\'Audit Opérationnel Récent',
          'ACTION & EVENT DETAILS': 'DÉTAILS DES ACTIONS & ÉVÉNEMENTS',
          'Action & Event Details': 'Détails des Actions & Événements',
          'STAFF / OPERATOR': 'PERSONNEL / OPÉRATEUR',
          'Staff / Operator': 'Personnel / Opérateur',
          'Portland Cement 42.5N (50kg Bag)': 'Ciment Portland 42.5N (Sac de 50kg)',
          'Portland Cement 42.5N': 'Ciment Portland 42.5N',
          'Hardware Contractor Credit & Debts': 'Crédit & Dettes Entrepreneurs Quincaillerie',
          'Track trade professional credit lines, building material debts, and payment records': 'Suivre les lignes de crédit des professionnels, les dettes de matériaux et les paiements',
          'Register Contractor Credit': 'Enregistrer un Crédit Entrepreneur',
          'Outstanding Contractor Debts': 'Dettes Entrepreneurs en Cours',
          'OUTSTANDING CONTRACTOR DEBTS': 'DETTES ENTREPRENEURS EN COURS',
          'TOTAL RECOVERED CREDIT': 'CRÉDIT RECOUVRÉ TOTAL',
          'Total Recovered Credit': 'Crédit Recouvré Total',
          'TOTAL CREDIT RECORDS': 'TOTAL DES ENREGISTREMENTS DE CRÉDIT',
          'Total Credit Records': 'Total Enregistrements de Crédit',
          'Search contractor, phone, credit type...': 'Rechercher un entrepreneur, téléphone, type de crédit...',
          'Search contractor, ph...': 'Rechercher un entrepreneur, téléphone...',
          'All Credit Records': 'Tous les Enregistrements',
          'Unpaid Balances': 'Soldes Impayés',
          'Fully Settled': 'Entièrement Réglé',
          'Credit Type / Item': 'Type de Crédit / Article',
          'CREDIT TYPE / ITEM': 'TYPE DE CRÉDIT / ARTICLE',
          'Outstanding Due': 'Solde Dû',
          'OUTSTANDING DUE': 'SOLDE DÛ',
          'Building Supplies': 'Matériaux de Construction',
          'Directory of registered builders, electricians, plumbers, and trade professionals': 'Annuaire des constructeurs, électriciens, plombiers et professionnels du bâtiment enregistrés',
          'Register Contractor': 'Enregistrer un Entrepreneur',
          'Register New Trade Contractor': 'Enregistrer un nouvel entrepreneur',
          'Registered Trade Contractors': 'Entrepreneurs Enregistrés',
          'Contractors With Active Debts': 'Entrepreneurs avec Dettes Actives',
          'Total Contractor Sales': 'Ventes Totales aux Entrepreneurs',
          'Contractor Name': 'Nom de l\'Entrepreneur',
          'Company / Contractor Name': 'Nom de l\'entreprise / entrepreneur',
          'Trade Type': 'Secteur d\'Activité',
          'Total Orders': 'Total des Commandes',
          'Total Purchases': 'Achats Totaux',
          'Credit Limit': 'Limite de Crédit',
          'Credit Line Limit': 'Limite de ligne de crédit',
          'Active Debt': 'Dette Active',
          'General Contractor': 'Entrepreneur Général',
          'Electrical Specialist': 'Spécialiste Électricien',
          'Plumbing Specialist': 'Spécialiste Plombier',
          'Masonry & Civil': 'Maçonnerie & Génie Civil',
          'Walk-in Buyer': 'Acheteur de Passage',
          'Walk-in Customer': 'Client de Passage',
          'Commande': 'Commande',
          'Commandes': 'Commandes',
          'Tool & Equipment Rentals': 'Location d\'Outillage & Équipements',
          'Manage machinery checkout, daily rental rates, security deposits, and tool return inspections': 'Gérer la sortie de machines, les tarifs journaliers, les dépôts de garantie et les retours',
          'Checkout Equipment': 'Louer un Équipement',
          'Checkout Equipment / Tool Rental': 'Location d\'Équipement & Outillage',
          'Active Equipment Rentals': 'Locations d\'Équipements Actives',
          'ACTIVE EQUIPMENT RENTALS': 'LOCATIONS D\'ÉQUIPEMENTS ACTIVES',
          'Active Security Deposits': 'Dépôts de Garantie Actifs',
          'ACTIVE SECURITY DEPOSITS': 'DÉPÔTS DE GARANTIE ACTIFS',
          'Total Rental Earnings': 'Gains Totaux de Location',
          'TOTAL RENTAL EARNINGS': 'GAINS TOTAUX DE LOCATION',
          'Total Rental Records': 'Total Enregistrements de Location',
          'TOTAL RENTAL RECORDS': 'TOTAL DES ENREGISTREMENTS',
          'Search equipment, serial #, contractor...': 'Rechercher équipement, n° de série, entrepreneur...',
          'All Equipment & Machinery': 'Tous les Équipements & Machines',
          'Currently Rented': 'En Cours de Location',
          'Returned': 'Retourné',
          'Returned & Inspected': 'Retourné & Inspecté',
          'Equipment / Tool': 'Équipement / Outil',
          'EQUIPMENT / TOOL': 'ÉQUIPEMENT / OUTIL',
          'Contractor / Customer': 'Entrepreneur / Client',
          'CONTRACTOR / CUSTOMER': 'ENTREPRENEUR / CLIENT',
          'Rental Rates & Deposit': 'Tarifs & Dépôt',
          'RENTAL RATES & DEPOSIT': 'TARIFS & DÉPÔT',
          'Status & Actions': 'Statut & Actions',
          'STATUS & ACTIONS': 'STATUT & ACTIONS',
          'Process Return': 'Traiter le Retour',
          'Tool Return Inspection': 'Inspection de Retour d\'Outil',
          '🧱 Concrete Mixer Machine (5.5 HP Diesel)': '🧱 Bétonnière (5.5 HP Diesel)',
          'Concrete Mixer Machine (5.5 HP Diesel)': 'Bétonnière (5.5 HP Diesel)',
          '⚡ Portable Silent Generator (10 KVA)': '⚡ Générateur Silencieux Portable (10 KVA)',
          'Portable Silent Generator (10 KVA)': 'Générateur Silencieux Portable (10 KVA)',
          '💥 Heavy Demolition Breaker / Jackhammer (1500W)': '💥 Marteau Piqueur / Démolisseur Lourd (1500W)',
          'Heavy Demolition Breaker / Jackhammer (1500W)': 'Marteau Piqueur / Démolisseur Lourd (1500W)',
          '🔨 Vibratory Plate Compactor / Rammer': '🔨 Plaque Vibrante Compacteuse / Pilonneuse',
          'Vibratory Plate Compactor / Rammer': 'Plaque Vibrante Compacteuse / Pilonneuse',
          '📐 Professional 3D Laser Rotary Level': '📐 Niveau Laser 3D Professionnel',
          'Professional 3D Laser Rotary Level': 'Niveau Laser 3D Professionnel',
          '🌊 Commercial High-Pressure Washer (250 Bar)': '🌊 Nettoyeur Haute Pression Commercial (250 Bar)',
          'Commercial High-Pressure Washer (250 Bar)': 'Nettoyeur Haute Pression Commercial (250 Bar)',
          '✂️ Heavy Steel Bar Cutter & Bender Saw': '✂️ Cisaillouse & Cintreuse d\'Acier Lourde',
          'Heavy Steel Bar Cutter & Bender Saw': 'Cisaillouse & Cintreuse d\'Acier Lourde',
          '🪜 Heavy-Duty Modular Scaffold Tower Set': '🪜 Échafaudage Modulaire Professionnel',
          'Heavy-Duty Modular Scaffold Tower Set': 'Échafaudage Modulaire Professionnel',

          // Hardware Bulk Import & Edit Modal Translations
          'Import Hardware Catalog (Excel / CSV)': 'Importer le Catalogue Quincaillerie (Excel / CSV)',
          'Register multiple hardware items, cables, and spools in batch': 'Enregistrer des articles, câbles et tourets quincaillerie en lot',
          'Instructions for Bulk Import': 'Instructions pour l\'Importation en Lot',
          'Download our sample template, fill in your hardware stock details, and re-upload the file below.': 'Téléchargez notre modèle d\'exemple, remplissez vos détails de stock quincaillerie et téléversez le fichier ci-dessous.',
          'Need a sample spreadsheet template?': 'Besoin d\'un modèle de feuille de calcul d\'exemple ?',
          'Includes sample rows for cables, cement, tools, and pipes': 'Comprend des exemples de lignes pour câbles, ciment, outils et tuyaux',
          'Download Excel Template': 'Télécharger le Modèle Excel',
          'Click or drag Excel / CSV file to upload': 'Cliquez ou glissez un fichier Excel / CSV à téléverser',
          'Supports .xlsx, .xls, and .csv files containing bulk hardware inventory': 'Prend en charge les fichiers .xlsx, .xls et .csv contenant le stock quincaillerie',
          'Items to Import': 'Articles à Importer',
          'Spool Tourets': 'Tourets de Coupe',
          'Total Import Valuation': 'Valeur Totale de l\'Import',
          'Parsed Hardware Items Preview': 'Aperçu des Articles Quincaillerie Extraits',
          'Clear & Reupload File': 'Effacer & Téléverser à nouveau',

          // ── Mobile Money & Agency Banking Ecosystem Translations ──
          '1. FLOAT & TILL MANAGEMENT': '1. GESTION DES FLOATS & CAISSES',
          '2. AGENT POS & REGISTER': '2. GUICHET POS & REGISTRE',
          '3. DEBTS & RECONCILIATION': '3. DETTES & CLÔTURES DE CAISSE',
          '4. EXPENSES & FINANCE': '4. DÉPENSES & ANALYSES',
          '5. STAFF & ADMINISTRATION': '5. PERSONNEL & ADMINISTRATION',
          'Expenses & Outflows': 'Dépenses & Décaissements',
          'Floats & Cash Tills': 'Solde Électronique & Caisses',
          'Agent POS Terminal': 'Terminal POS Agent',
          'Digital Notebook': 'Registre Numérique',
          'Customer History': 'Historique Clients',
          'Shift Audit & Reconciliation': 'Clôture de Caisse & Audit',
          'Client Debts & Credits': 'Dettes & Crédits Client',
          'Mobile Money & Financial Services': 'Mobile Money & Services Financiers',
          'Treasury Management, Float Operators Registry, Tills & Agent Commissions': 'Gestion de Trésorerie, Registre des Float Operators, Caisses & Commissions d\'Agent',
          'Kiosk Terminal Active': 'Terminal Kiosque Actif',
          'Gateway Sync Ready (Port 8009)': 'Passerelle Synchronisée (Port 8009)',
          'Gateway Sync Ready': 'Passerelle Synchronisée',
          'Dependent Kiosk (Shared Main Float)': '📍 Kiosque Dépendant (Float Partagé Siège)',
          'Independent Kiosk (Dedicated Float)': '🏬 Kiosque Indépendant (Float Dédié)',
          'All Kiosks / Global': '🌐 Tous les Kiosques / Global',
          'Assigned Kiosk': 'Kiosque Assigné',
          'Refresh': 'Actualiser',
          'Digital Register': 'Registre Digital',
          'Agent Client Debts': 'Dettes Client Agent',
          'Open Agent POS Terminal': 'Ouvrir Terminal POS Agent',
          'Portfolio by Currency': 'Portefeuille par Devise',
          'Each agent float and cash till is managed separately by currency': 'Chaque trésorerie agent et caisse physique est gérée séparément par devise',
          'Total Liquid Agent Capital': 'Capital Liquide Agent Total',
          'Total Agent Capital': 'Capital Total Agent',
          'Physical Cash in Till': 'Espèces en Caisse Physique',
          'PHYSICAL CASH IN TILL': 'ESPÈCES EN CAISSE PHYSIQUE',
          'Digital E-Float Balance': 'Solde Électronique E-Float',
          'SOLDE ÉLECTRONIQUE E-FLOAT': 'SOLDE ÉLECTRONIQUE E-FLOAT',
          'Total E-Floats Balance': 'Solde Total E-Floats',
          'TOTAL E-FLOATS BALANCE': 'SOLDE TOTAL E-FLOATS',
          'Earned Commissions': 'Commissions Gagnées',
          'COMMISSIONS GAGNÉES': 'COMMISSIONS GAGNÉES',
          'Total Earned Commissions': 'Total Commissions Gagnées',
          'Net Agent Income': 'Revenu Net d\'Agent',
          'Auto-Calculated': 'Calcul Automatique',
          'Till #01 Open': 'Caisse #01 Ouverte',
          'Shift Closure': 'Clôture Shift',
          'Rebalance': 'Rééquilibrer',
          'Active Operators': 'Opérateurs Actifs',
          'Operator Float Accounts & Partner Networks': 'Comptes Operator Float & Réseaux Partenaires',
          'Real-time overview of credit unit balances and restocking thresholds': 'Aperçu en temps réel des soldes d\'unités de crédit et seuils de réapprovisionnement par réseau',
          'Manage Operators': 'Gérer les Opérateurs',
          'No Mobile Money Operator Configured': 'Aucun Opérateur Mobile Money Configuré',
          'Add your MTN MoMo, Airtel Money, M-Pesa, Orange or Wave float accounts to start agent kiosk management.': 'Ajoutez vos comptes de float MTN MoMo, Airtel Money, M-Pesa, Orange ou Wave pour démarrer la gestion de votre kiosque agent.',
          'Initialize Float Operator Accounts': 'Initialiser les Comptes Float Opérateurs',
          'Deposit': 'Dépôt',
          'Withdrawal': 'Retrait',
          'Recent Mobile Money Transactions': '📋 Registre en Temps Réel des Transactions Agent',
          'History of deposits, withdrawals, and airtime top-ups performed on your kiosks': 'Historique des dépôts, retraits et recharges de crédit effectués sur vos kiosques',
          'View Full Ledger': 'Voir le Registre Complet',
          'No Recent Transactions Recorded': 'Aucune Transaction Enregistrée',
          'The transaction log will automatically record your deposits, withdrawals, and top-ups performed from the POS terminal.': 'Le registre des transactions enregistrera automatiquement vos dépôts, retraits et recharges effectuées depuis le terminal.',
          'Launch a POS Operation': 'Lancer une Opération POS',

          // Floats Page
          'E-Floats & Cash Till Management': 'Gestion des E-Floats & Caisse Physique',
          'New Account': 'Nouveau Compte',
          '+ New Account': '+ Nouveau Compte',
          'Commissions': 'Commissions',
          'Categories': 'Catégories',
          'Rebalance Treasury': 'Rééquilibrer la Trésorerie',
          'Multi-Currency Stock': 'Stock Multi-Devises',
          'Consolidated Multi-Currency Till + Floats': 'Caisse + Floats Consolidés Multi-Devises',
          'Float Rebalance': 'Rééquilibrage de Trésorerie',
          'Liquid transfer between cash till and SIM cards.': 'Transfert liquide entre caisse physique et cartes SIM opérateur.',
          'Execute Transfer': 'Exécuter le Transfert',
          'Consolidated Multi-Currency Portfolio': 'Portefeuille Multi-Devises Consolidé',
          'CONSOLIDATED MULTI-CURRENCY PORTFOLIO': 'PORTEFEUILLE MULTI-DEVISES CONSOLIDÉ',
          'Assets and liquidity per active operating currency': 'Actifs et liquidités par devise opérationnelle active',
          'Active Currencies': 'Devises Actives',
          'Active Network Operators & Bank Accounts': 'Comptes Opérateurs & Réseaux Partenaires Actifs',
          'Operator / SIM Account': 'Opérateur / Compte SIM',
          'Network Code': 'Code Réseau',
          'Currency': 'Devise',
          'Current E-Float Balance': 'Solde E-Float Actuel',
          'Actions': 'Actions',
          'Click to change account currency': 'Cliquez pour modifier la devise du compte',
          'Deactivate account': 'Désactiver le compte',
          'Activate account': 'Activer le compte',
          'Edit operator account': 'Modifier le compte opérateur',
          'Deactivate / Remove float account?': 'Désactiver / Supprimer le compte float ?',
          'The account will be deactivated from the active network.': 'Le compte sera désactivé du réseau actif.',
          'Yes, Remove': 'Oui, Supprimer',
          'Deactivate / Soft Remove': 'Désactiver / Suppression douce',
          'No Operator Configured': 'Aucun Opérateur Configuré',
          'Add your MTN MoMo, Airtel Money, M-Pesa, Orange or Wave accounts.': 'Ajoutez vos comptes MTN MoMo, Airtel Money, M-Pesa, Orange ou Wave.',

          // Ledger Page
          'Digital Transaction Notebook': 'Registre Numérique des Transactions',
          'Audit trail & fee commissions ledger': 'Piste d\'audit et registre des commissions & frais',
          'Export Excel': 'Exporter en Excel',
          'Record Transaction': 'Enregistrer une Transaction',
          '+ Record Transaction': '+ Enregistrer une Transaction',
          'Financial Summary': 'Résumé Financier',
          'Processed Volume': 'Volume Traité',
          'PROCESSED VOLUME': 'VOLUME TRAITÉ',
          'Total Cash-In': 'Total Dépôts (Cash-In)',
          'TOTAL CASH-IN': 'TOTAL CASH-IN (DÉPÔTS)',
          'Total Cash-Out': 'Total Retraits (Cash-Out)',
          'TOTAL CASH-OUT': 'TOTAL CASH-OUT (RETRAITS)',
          'Total Commissions': 'Total Commissions Gagnées',
          'TOTAL COMMISSIONS': 'TOTAL COMMISSIONS GAGNÉES',
          'Cash-In': 'Dépôt',
          'Cash-Out': 'Retrait',
          'Search phone, name, ref...': 'Rechercher par téléphone, nom, réf...',
          'Filter by Type': 'Filtrer par type',
          'Filter by Operator': 'Filtrer par opérateur',
          'Filter by Date': 'Filtrer par date',
          'Filtrer par date': 'Filtrer par date',
          'Reset All Filters': 'Réinitialiser tous les filtres',
          'Transaction History Log': 'Historique des Transactions',
          'No transactions match your current filters': 'Aucune transaction ne correspond à vos filtres actuels',
          'Try resetting date, operator, or type filters, or record a new transaction.': 'Essayez de réinitialiser les filtres de date, d\'opérateur ou de type, ou enregistrez une nouvelle transaction.',
          'Summary Currency': 'Devise du Résumé',
          'Record New Mobile Money Transaction': 'Enregistrer une Transaction',
          'Operator SIM Account': 'Compte SIM Opérateur',
          'Select operator (e.g. MTN, Airtel, M-Pesa)...': 'Sélectionner l\'opérateur (MTN, Airtel, M-Pesa...)',
          'Please select operator account': 'Veuillez sélectionner un compte opérateur',
          'Transaction Type': 'Type d\'Opération',
          'Customer Phone Number': 'Téléphone Client',
          'Customer Full Name': 'Nom Complet du Client',
          'Operator Ref ID': 'Réf / ID Opérateur',
          'Transaction Fee': 'Frais de Transaction',
          'Transaction Remarks / Notes': 'Remarques / Notes (Optionnel)',
          'Optional notes...': 'Remarques éventuelles...',
          'Save & Submit Transaction': 'Enregistrer l\'Opération',
          'Registre Guichet Trésorerie • Instant Real-Time Ledger Sync': 'Registre Guichet • Synchronisation Immédiate',
          'Defaults to 10% of fee, fully customizable per tenant/transaction': 'Par défaut 10% des frais, entièrement personnalisable',

          // POS Page
          'Terminal POS Agent & Guichet Kiosque': 'Terminal POS Agent & Guichet Kiosque',
          'Mobile Money Agent POS & Kiosk Terminal': 'Terminal POS Agent & Guichet Kiosque',
          'No Active Shift: Please start your agent shift to record till operations.': 'Aucune Session Guichet Active : Vous n\'avez pas de shift ouvert. Veuillez démarrer votre shift agent pour enregistrer vos opérations de caisse.',
          'Open Agent Shift': 'Ouvrir mon Shift Agent',
          '1. Agent Operation Type': '1. TYPE D\'OPÉRATION AGENT',
          '2. Operator Account / SIM': '2. COMPTE OPÉRATEUR / SIM',
          'Customer Phone': 'Téléphone Client',
          'Customer Name (Optional)': 'Nom Client (Optionnel)',
          'Transaction Amount': 'Montant Transaction',
          'Operator Reference / ID': 'Référence / ID Opérateur',
          'Customer Fee': 'Frais Client',
          'Register as Customer Debt / Credit (Unpaid)': 'Enregistrer comme Dette / Crédit Client (Non Payé)',
          'Transaction completed on credit without immediate cash collection': 'Transaction effectuée à crédit sans encaissement cash immédiat',
          'Observation / Note on Operation (Optional)': 'Observation / Note sur l\'Opération (Optionnel)',
          'Agent Commission': 'Commission Agent',
          'Commission Rate (%)': 'Taux Commission (%)',
          'Net Commission': 'Commission Nette',
          'Commission Net': 'Commission Nette',
          'Cash Till': 'Caisse Espèces',
          'Float SIM': 'Float SIM',
          'Validate CASH IN Transaction': 'Valider la Transaction DÉPÔT (CASH IN)',
          'Validate CASH OUT Transaction': 'Valider la Transaction RETRAIT (CASH OUT)',
          'Validate AIRTIME Transaction': 'Valider la Transaction RECHARGE (AIRTIME)',
          'Validate EXPENSE Transaction': 'Valider la Transaction DÉPENSE CAISSE',
          'Validate TRANSFER Transaction': 'Valider la Transaction TRANSFERT D\'ARGENT',
          'Validate CASH_IN Transaction': 'Valider la Transaction DÉPÔT (CASH IN)',
          'Validate CASH_OUT Transaction': 'Valider la Transaction RETRAIT (CASH OUT)',
          'Validate BILL_PAYMENT Transaction': 'Valider la Transaction FACTURE (BILL PAY)',
          'Transfer': 'Transfert',
          'Bill Payment': 'Facture',
          'Airtime': 'Crédit / Airtime',
          'Rate %': '% Taux',
          '% Rate': '% Taux',
          'Fixed': 'Fixe',
          'Fixed Amount': 'Montant Fixe',
          'Sender Phone': 'Téléphone Expéditeur',
          'Sender Name (Optional)': 'Nom Expéditeur (Optionnel)',
          'Receiver Phone *': 'Téléphone Destinataire / Bénéficiaire *',
          'Receiver Name (Optional)': 'Nom Destinataire / Bénéficiaire (Optionnel)',
          'Receiver name...': 'Nom du destinataire...',
          'Full name...': 'Nom complet...',
          'ex: Purpose, manager approval or special remarks...': 'ex: Motif, accord manager ou remarque spéciale...',
          'Insufficient Float SIM balance on': 'Solde Float SIM insuffisant sur',
          'Insufficient Cash Till balance to disburse': 'Solde Caisse Cash insuffisant pour remettre',
          'Entered': 'Saisi',
          'Open Debt': 'Dette Impayée',
          'View History': 'Voir Historique',
          'Reference already exists!': 'Référence existe déjà !',
          'Main': 'Siège',

          // Debts Page
          'Customer Debts & Credits Management': 'Gestion des Dettes & Crédits Client',
          'active cases': 'dossiers actifs',
          'cases': 'dossiers',
          'POS Operation': 'Opération Guichet',
          '+ POS Operation': '+ Opération Guichet',
          'Active Debtor Clients': 'Clients en Dette Active',
          'Average Debt / Client': 'Dette Moyenne / Client',
          'Partial': 'Partielles',
          'Settled': 'Réglées / Historique',
          'All': 'Toutes',
          'Date & Time': 'Date & Heure',
          'Debt Amount': 'Montant Dette',
          'Already Paid': 'Déjà Payé',
          'Remaining Unpaid Balance': 'Solde Reste Impayé',
          'SETTLED (PAID)': 'RÉGLÉ (PAYÉ)',
          'Actions & History': 'Actions & Historique',
          'Pay': 'Régler',
          'History Notes': 'Historique Notes',
          'Record Customer Debt Payment': 'Enregistrer un Règlement de Dette Client',
          'Initial Debt': 'Dette Initiale',
          'Already Settled': 'Déjà Réglé',
          'Remaining to Pay': 'Reste à Payer',
          'Paid Amount': 'Montant Versé',
          'Please enter the amount': 'Veuillez saisir le montant',
          'Please enter the paid amount': 'Veuillez saisir le montant versé',
          'Note (ex: Cash handed at till)': 'Note (ex: Remis en Cash au Guichet)',
          'Payment note...': 'Note de versement...',
          'In Cash Till': 'En Caisse',
          'Debt Settlement History': 'Historique de Règlement de Dette',
          'SETTLED IN FULL (PAID)': 'RÉGLÉ EN TOTALITÉ (PAYÉ)',
          'Notes & Collection Log': 'Notes & Journal d\'Encaissement',
          'No specific note recorded.': 'Aucune note spécifique enregistrée.',

          // Customer History Page
          'Customer Transaction History & Portfolio': 'Historique des Transactions & Portefeuille Client',
          'Search by customer phone, name or reference (ex: 0900000000)...': 'Rechercher par Téléphone client, Nom ou Réf Opérateur (ex: 0900000000)...',
          'Search Customer': 'Rechercher Client',
          'operations': 'opérations',
          'Cumulative Volume': 'Volume Cumulé',
          'Total Cash-In (Deposits)': 'Total Cash-In (Dépôts)',
          'Active Unpaid Debt': 'Dette Active Impayée',
          'Customer Operations Log': 'Registre des Opérations Clients',
          'All Transactions': 'Toutes les transactions',
          'Principal Amount': 'Montant Principal',
          'Debt Status': 'Statut Dette',
          'DEBT PAID': 'DETTE PAYÉE',
          'DEBT': 'DETTE',
          'Airtime Recharge': 'Recharge Crédit (Airtime)',
          'Yesterday': 'Hier',
          'This Week': 'Cette Semaine',
          'This Month': 'Ce Mois',

          // Shift Audit & Reconciliation Page
          'Loading reconciliation state...': 'Chargement de l\'état de réconciliation...',
          'Till Closure & Shift Reconciliation': 'Clôture de Caisse & Réconciliation Guichet',
          'Physical cash till audit, SIM E-Floats reconciliation, and discrepancy log': 'Audit physique de la caisse cash, réconciliation des E-Floats SIM et journal d\'écarts',
          'ACTIVE SHIFT IN PROGRESS': 'SHIFT ACTIF EN COURS',
          'NO ACTIVE SHIFT': 'AUCUN SHIFT EN COURS',
          'Shift Closure & Audit in Progress': 'Clôture & Audit du Shift En Cours',
          'Open New Shift': 'Ouverture d\'un Nouveau Shift',
          'Opening Protocol': 'Protocole d\'Ouverture',
          'Mandatory audit before any counter operation': 'Audit obligatoire avant toute opération guichet',
          'Physical Cash Count': 'Comptage Cash Physique',
          'Count physical cash bills and coins in the cash drawer at start.': 'Comptez les billets et pièces physiques présents dans le tiroir caisse au démarrage.',
          'Verify Float SIM': 'Vérification Float SIM',
          'Verify virtual unit balances on each operator SIM card (MTN, Airtel, M-Pesa, Orange, Wave).': 'Vérifiez les soldes virtuels sur chaque carte SIM opérateur (MTN, Airtel, M-Pesa, Orange, Wave).',
          'Baseline Alignment': 'Alignement Baseline',
          'These amounts serve as baseline reference for discrepancy calculation upon closing.': 'Ces montants serviront de référence baseline pour le calcul d\'écart lors de la clôture.',
          'Start a New Agent Shift': 'Démarrer un Nouveau Shift Agent',
          'Auto-fill system balances': 'Auto-remplir les soldes système',
          'Teller / Responsible Agent Name (Fuko User) *': 'Nom du Guichetier / Agent Responsable (Utilisateur Fuko) *',
          'Teller name is required': 'Le nom du guichetier est requis',
          'Select a company user / agent...': 'Sélectionner un utilisateur / agent de l\'entreprise...',
          '(Active Logged-in User)': '(Utilisateur Actif Connecté)',
          'Till Currency': 'Devise de la Caisse',
          'Search currency...': 'Rechercher devise...',
          'Opening Cash Float at Start': 'Fond de Caisse Cash au Démarrage',
          'Opening cash float is required': 'Fond de caisse requis',
          'Opening E-Float Balances per Operator SIM': 'Soldes E-Float d\'Ouverture par SIM Opérateur',
          'No active operator accounts recorded.': 'Aucun compte opérateur actif enregistré.',
          'Current Shift Metrics': 'Métrique du Shift En Cours',
          'Shift ID:': 'Identifiant Shift:',
          'Teller / Agent:': 'Guichetier / Agent:',
          'Opened At:': 'Ouvert le:',
          'Initial Cash Float:': 'Fond de Caisse Initial:',
          'System Expected Cash in Till': 'Cash Attendues en Caisse (Système)',
          'Calculated from initial float + net session transactions': 'Calculé à partir du fond initial + transactions nettes de la session',
          'Real-time Cash Variance': 'Écart de Caisse en Temps Réel',
          '(Surplus)': '(Excédent)',
          '(Shortage)': '(Manquant)',
          '(Perfect ✓)': '(Parfait ✓)',
          'End of Day Audit & Shift Closure': 'Audit de Fin de Journée & Clôture',
          'Audit Currency': 'Devise d\'Audit',
          'Actual Physical Drawer Cash Count': 'Comptage Physique Réel dans le Tiroir',
          'Physical cash count is required': 'Comptage physique requis',
          'Enter counted physical cash amount...': 'Saisir le montant physique compté...',
          'Final Physical Count of SIM E-Floats': 'Comptage Physique Final des E-Floats SIM',
          'Audit Remarks / Discrepancy Reason': 'Remarques d\'Audit / Motif en cas d\'Écart',
          'Explain any cash shortage, surplus, or adjustment...': 'Expliquer tout manquant, excédent ou ajustement de caisse...',
          'Reconcile & Close Agent Shift': 'Réconcilier & Clôturer le Shift Agent',
          'Closure Log & Historical Audit': 'Journal des Clôtures & Audit Historique',
          'Complete History of Closed Sessions': 'Historique Complet des Sessions Clôturées',
          'Audit registry with expected cash, actual physical count, teller agent, and calculated variances': 'Registre d\'audit avec montants de caisse attendus, comptage physique réel, agent guichetier et écarts calculés',
          'Shift ID': 'ID Shift',
          'Opening Date': 'Date d\'Ouverture',
          'Closing Date': 'Date de Clôture',
          'Initial Cash': 'Cash Initial',
          'System Expected': 'Système Attendu',
          'Physical Count': 'Comptage Physique',
          'Cash Discrepancy': 'Écart de Caisse',
          'Actions / Details': 'Actions / Détails',
          'Audit Details': 'Détails Audit',
          '🟢 IN PROGRESS': '🟢 EN COURS',
          '🔒 CLOSED': '🔒 CLÔTURÉ',
          'Cash Surplus': 'Excédent Caisse',
          'Cash Shortage': 'Manquant Caisse',
          'Audit & Reconciliation Report': 'Rapport d\'Audit & Réconciliation',
          'Counter Session': 'Session Guichet',
          'Print Report': 'Imprimer Rapport',
          'Responsible Teller': 'Guichetier Responsable',
          'MoMo Operations Agent': 'Agent d\'Exploitation MoMo',
          'Opening': 'Ouverture',
          'Closing': 'Clôture',
          'Physical Cash Drawer Audit & Count': 'Audit & Comptage Caisse Cash physique',
          'Counted bills & coins': 'Billets & pièces décomptés',
          'Initial float + Transactions': 'Fond initial + Transactions',
          'Final Cash Discrepancy': 'Écart Final de Caisse',
          'Operator SIM E-Money Float Audit': 'Audit des Floats E-Money SIM par Opérateur',
          'SIM Operator': 'Opérateur SIM',
          'Opening Float': 'Float Ouverture',
          'Closing Float': 'Float Clôture',
          'Net Variation': 'Variation Net',
          'Remarks & Manager Notes': 'Remarques & Notes Manager',
          'System balances copied into audit fields!': 'Soldes système copiés dans les champs d\'audit !',
          'Agent shift opened successfully!': 'Shift Agent ouvert avec succès !',
          'Failed to open shift': 'Échec de l\'ouverture du shift',
          'Perfect reconciliation! Zero cash discrepancy.': 'Réconciliation parfaite ! Zéro écart de caisse.',
          'Closed with cash surplus': 'Clôture avec excédent de caisse',
          'Closed with cash shortage': 'Clôture avec manquant de caisse',
          'Failed to close shift': 'Échec de la clôture du shift',
          'US Dollar': 'Dollar Américain',
          'Congolese Franc': 'Franc Congolais',
          'Kenyan Shilling': 'Shilling Kenyan',
          'Central African CFA Franc': 'Franc CFA d\'Afrique Centrale',
          'West African CFA Franc': 'Franc CFA d\'Afrique de l\'Ouest',
          'Rwandan Franc': 'Franc Rwandais',
          'Ugandan Shilling': 'Shilling Ougandais',

          // POS & E-Receipt Slip
          'Please select a mobile money operator account': 'Veuillez sélectionner un compte opérateur mobile money',
          'Cannot submit: Reference number already exists!': 'Impossible de valider : La référence existe déjà !',
          'Insufficient Float on SIM': 'Solde Float SIM insuffisant',
          'Insufficient Cash Till balance to disburse to customer!': 'Solde Caisse Cash insuffisant pour décaisser au client !',
          'AGENCY & COUNTER KIOSK': 'AGENCE & GUICHET KIOSQUE',
          'TICKET #': 'TICKET n°',
          'OPERATOR REF': 'RÉF OPÉRATEUR',
          'CUSTOMER CREDIT - UNPAID': 'CRÉDIT CLIENT - NON PAYÉ',
          'Print Ticket': 'Imprimer Ticket',

          // Expenses & Outflows
          'Mobile Money Expenses & Outflows': 'Dépenses & Décaissements Mobile Money',
          'Backend-calculated agent expenses, CIT transport, rent & float rebalances': 'Dépenses d\'exploitation calculées, transport de fonds, loyer & rééquilibrage de float',
          'Total Operating Outflows': 'Total des Décaissements d\'Exploitation',
          'Cash Till Outflows': 'Décaissements Caisse Cash',
          'E-Float Charges': 'Frais & Charges E-Float',
          'Recorded Outflows': 'Décaissements Enregistrés',
          'Backend Calculated': 'Calculé par le Système',
          'Deducted directly from physical counter cash till': 'Déduit directement de la caisse cash physique du guichet',
          'Operator rebalance & bank transfer charges': 'Frais de rééquilibrage opérateur et virements bancaires',
          'Operating Expense History Log': 'Journal Historique des Dépenses d\'Exploitation',
          'No agent expenses recorded for selected period': 'Aucune dépense d\'exploitation enregistrée pour la période sélectionnée',
          '+ Record Agent Expense': '+ Enregistrer une Dépense Agent',
          '+ Record First Expense': '+ Enregistrer la Première Dépense',
          'Record Agent Expense & Outflow': 'Enregistrer une Dépense & Décaissement Agent',
          'Log operational expenses, CIT transport, rent, sub-agent payouts & float charges': 'Enregistrez les dépenses opérationnelles, transport CIT, loyer, commissions sous-agents & frais de float',
          'Expense Category': 'Catégorie de Dépense',
          'Category required': 'Catégorie requise',
          'Select expense category...': 'Sélectionner une catégorie de dépense...',
          'Specify Custom Expense Category Name': 'Préciser le Nom de la Catégorie Personnalisée',
          'Please type custom category name': 'Veuillez saisir le nom de la catégorie personnalisée',
          'Payment Source Account': 'Compte Source de Paiement',
          'Select payment source...': 'Sélectionner la source de paiement...',
          'Physical Cash Till': 'Caisse Cash Physique',
          'Outflow Currency': 'Devise de Décaissement',
          'Currency required': 'Devise requise',
          'Amount required': 'Montant requis',
          'Payee / Vendor Name': 'Nom du Bénéficiaire / Fournisseur',
          'Expense Description & Remarks': 'Description & Remarques de la Dépense',
          'Describe expense purpose, receipt number or notes...': 'Décrivez le motif de la dépense, numéro de reçu ou notes...',
          'Save & Log Agent Expense': 'Enregistrer la Dépense Agent',
          'Bank Transport / Cash in Transit': 'Transport Bancaire / Fonds en Transit (CIT)',
          'Operator Rebalance & Liquidity Fee': 'Frais de Rééquilibrage & Liquidité Opérateur',
          'Sub-Agent / Teller Commission Payout': 'Paiement Commissions Sous-Agents / Guichetiers',
          'Agent Rent & Building Utilities': 'Loyer du Kiosque & Charges / Électricité',
          'Till Shortage & Discrepancy Adjustment': 'Ajustement Manquant Caisse & Écart',
          'Hardware, SIM & POS Maintenance': 'Maintenance Matériel, SIM & Terminal POS',
          'Licenses, Taxes & Local Fees': 'Taxes Locales, Licences & Frais Municipaux',
          'Custom / Write-in Agent Expense': 'Dépense Agent Personnalisée / Autre',
          'Custom Expense': 'Dépense Personnalisée',
          'Filter by Category': 'Filtrer par Catégorie',
          'New Agent Expense recorded successfully!': 'Nouvelle dépense agent enregistrée avec succès !',
          'Failed to record agent expense': 'Échec de l\'enregistrement de la dépense agent',
          'No expenses data to export': 'Aucune donnée de dépenses à exporter',
          'Expenses log exported to Excel CSV successfully': 'Journal des dépenses exporté en Excel CSV avec succès',
          'Expense ID / Date': 'ID Dépense / Date',
          'Payee / Beneficiary': 'Bénéficiaire / Fournisseur',
          'Payment Method': 'Mode de Paiement',
          'Outflow Amount': 'Montant Décaissé',
          'Loading agent expenses from backend API...': 'Chargement des dépenses agent depuis l\'API...',
          'Bank / Operator': 'Banque / Opérateur',
          'CASH TILL': 'CAISSE CASH',
          'E-FLOAT': 'E-FLOAT',

          // Financial Statements & Reports
          'Executive Multi-Currency Financial Statement': 'État Financier Exécutif Multi-Devises',
          'Multi-currency profit statements, commissions, operating expenses & net margin yields': 'États de résultat multi-devises, commissions, charges d\'exploitation & marges nettes',
          'Headquarters': 'Siège',
          'Gross Revenue': 'Revenu Brut',
          'Operating Expenses': 'Dépenses d\'Exploitation',
          'NET AGENT PROFIT': 'BÉNÉFICE NET AGENT',
          'Net Profit Margin': 'Marge Bénéficiaire Nette',
          'Fees': 'Frais',
          'Till & Rebalance Costs': 'Coûts Caisse & Rééquilibrage',
          'Yield': 'Rendement',
          'Avg': 'Moy',
          'Profit & Revenue Trends': 'Tendances Revenus & Bénéfices',
          'Category & Channel Distribution': 'Répartition par Catégorie & Canal',
          'Operator Share & Profit Yield': 'Part de Marché Opérateur & Rendement',
          'Audit Ledger': 'Journal d\'Audit',
          'Daily Cash-In vs Cash-Out Volume Curve': 'Courbe Journalière des Volumes Dépôts vs Retraits',
          'Daily Net Profit Earnings': 'Gains Journaliers en Bénéfice Net',
          'Cash-In (Deposits)': 'Dépôts (Cash-In)',
          'Cash-Out (Withdrawals)': 'Retraits (Cash-Out)',
          'Transaction Type Donut Chart': 'Graphique Donut par Type de Transaction',
          'Share': 'Part',
          'Operator Market Share': 'Part de Marché Opérateur',
          'No operator share data for selected period': 'Aucune donnée de part de marché pour la période sélectionnée',
          'Calculating backend profit & loss statements...': 'Calcul des états de profits et pertes...',
          'No daily profit data available for selected period': 'Aucune donnée de profit journalier pour la période sélectionnée',
          'CASH-IN': 'DÉPÔT (CASH-IN)',
          'CASH-OUT': 'RETRAIT (CASH-OUT)',
          'AIRTIME': 'CRÉDIT AIRTIME',
          'EXPENSE': 'DÉPENSE',
          'Customer / Ref': 'Client / Réf',
          'No transaction data to export': 'Aucune transaction à exporter',
          'Financial report exported to Excel CSV successfully': 'Rapport financier exporté en Excel CSV avec succès',
          'Failed to load backend financial report analytics': 'Échec du chargement des analyses financières',
          'Last 7 Days': '7 Derniers Jours',
          '30 Days': '30 Jours',
          'Financial Statement': 'État Financier',

          // Mobile Money Native App Experience
          'Total Liquid Capital': 'Capital Total Disponible',
          'E-Float SIM': 'E-Floats SIM',
          'Add Float SIM': 'Ajouter un Float SIM',
          'Operator SIM Floats': 'Comptes Opérateurs SIM',
          'Recent Transactions': 'Dernières Transactions',
          'POS': 'POS',
          'Terminal': 'Terminal',
          'Registre': 'Registre',
          'Ledger': 'Journal',
          'Dettes': 'Dettes',
          'Clients': 'Clients',
          'Dépenses': 'Dépenses',
          'Outflows': 'Décaissements',
          'Floats SIM': 'Floats SIM',
          'Rapports': 'Rapports',
          'Audit & P&L': 'Audit & P&L',
          'Clôture': 'Clôture',
          'Shift Audit': 'Audit Shift',
          'Actualiser': 'Actualiser',
          'Sync': 'Synchro',

          // Operator Modals & Float Management Translations
          'Operator Name': 'Nom de l\'opérateur',
          'Operator Category': 'Catégorie d\'opérateur',
          'SIM Number / Till Agent': 'N° SIM / N° de caisse',
          'Account Currency': 'Devise du compte',
          'Initial Float Balance': 'Solde float initial',
          'Register Operator': 'Enregistrer l\'opérateur',
          'Edit Operator Account': 'Modifier le compte opérateur',
          'Account Status': 'Statut du compte',
          'INACTIVE': 'INACTIF',
          'Rebalance Currencies & Floats': 'Rééquilibrer les devises & floats',
          'Source Account (Debit)': 'Compte source (Débit)',
          'Select source account': 'Sélectionner le compte source',
          'Destination Account (Credit)': 'Compte destinataire (Crédit)',
          'Select destination account': 'Sélectionner le compte destinataire',
          'Transfer Amount & Currency': 'Montant & devise du transfert',
          'Frequent Currencies': 'Devises fréquentes',
          'All Currencies': 'Toutes les devises',
          'Transfer amount is required': 'Le montant du transfert est requis',
          'Transfer Reason / Notes': 'Motif du transfert / Notes',
          'Confirm Rebalance Transfer': 'Confirmer le transfert de rééquilibrage',
          'Register New Kiosk / Branch': 'Enregistrer un nouveau kiosque / succursale',
          'Branch / Kiosk Name': 'Nom du kiosque / succursale',
          'Branch Code': 'Code succursale',
          'Physical Address / Location': 'Adresse physique / Emplacement',
          'Branch Structure Type': 'Type de structure de succursale',
          'Independent Kiosk (Standalone SIM & Float)': 'Kiosque indépendant (SIM & Float autonomes)',
          'Dependent / Sub-Agency (Shares Master E-Float with Siège)': 'Sous-agence dépendante (Partage le float du siège)',
          'Save & Register Branch': 'Enregistrer la succursale',
          'Register New Operator Category': 'Enregistrer une nouvelle catégorie d\'opérateur',
          'Category Display Name': 'Nom d\'affichage de la catégorie',
          'Category System Code': 'Code système de la catégorie',
          'Save Category': 'Enregistrer la catégorie',
          'Operator Categories': 'Catégories d\'opérateurs',
          'Configure global and custom telecom & banking category structure': 'Configurer la structure des catégories télécoms et bancaires',
          'Search currency (e.g. USD, CDF, KES, EUR)...': 'Rechercher une devise (ex: USD, CDF, KES, EUR)...',
          'Frequent / Preferred Currencies': 'Devises fréquentes / préférées',
          'All World Currencies': 'Toutes les devises du monde',
          'Operator name is required': 'Le nom de l\'opérateur est requis',
          'Network code is required': 'Le code réseau est requis',
          'Branch name is required': 'Le nom de la succursale est requis',
          'Category label is required': 'Le libellé de la catégorie est requis',
          'Category code is required': 'Le code de la catégorie est requis',
          'Add Mobile Money Operator': 'Ajouter un opérateur Mobile Money',
          'Telecom / Mobile Money': 'Télécom / Mobile Money',
          'Agency Banking / Bank': 'Banque d\'agence / Banque',
          'Public Utility / Bills': 'Services publics / Factures',
          'Commercial Bank': 'Banque Commerciale',
          'Microfinance / MFI': 'Microfinance / IMF',
          'Utility / Bill Pay': 'Services Publics / Factures',
          'Payment Gateway': 'Passerelle de Paiement',
          'Service Public / Factures': 'Services Publics / Factures',
          'Agency Banking / Banque': 'Banque d\'Agence / Banque',
          'e.g. MTN Mobile Money, Airtel Money, Wave': 'ex: MTN Mobile Money, Airtel Money, Wave',
          'e.g. MTN, AIRTEL, MPESA, WAVE': 'ex: MTN, AIRTEL, MPESA, WAVE',
          'e.g. 0700000000 or Till 19283': 'ex: 0700000000 ou Caisse 19283',
          'e.g. Bank cash deposit to replenish MTN float': 'ex: Dépôt bancaire pour réapprovisionner le float MTN',
          'e.g. Downtown Kiosk #3, Goma Branch': 'ex: Kiosque Centre-Ville #3, Agence Goma',
          'e.g. BR-03, KNK-3': 'ex: BR-03, KNK-3',
          'e.g. Avenue du Commerce, Building B': 'ex: Avenue du Commerce, Bâtiment B',
          'e.g. Crypto Gateway, Remittance Agency': 'ex: Passerelle Crypto, Agence de Transfert',
          'e.g. CRYPTO, REMITTANCE': 'ex: CRYPTO, TRANSFERT',
          'Actifs': 'Actifs'
        }
      };

      // Determine active language reliably
      const localLang =
        (typeof window !== 'undefined' && localStorage.getItem('currentLanguage')) ||
        currentLanguage ||
        selectedLanguage ||
        (typeof window !== 'undefined' && (localStorage.getItem('preferredLang') || localStorage.getItem('lang'))) ||
        'en';

      const isFr = String(localLang).toLowerCase().includes('fr') || localLang === '2';

      if (isFr) {
        // Direct exact match in French dictionaries
        const frDirect =
          billingFallbacks['fr']?.[text] ||
          hotelFallbacks['fr']?.[text] ||
          storefrontFallbacks['fr']?.[text] ||
          mobileMoneyFallbacks['fr']?.[text] ||
          pharmacyFallbacks['fr']?.[text] ||
          jewelryFallbacks['fr']?.[text] ||
          fallbacks['fr']?.[text];
        if (frDirect) return frDirect;

        const lowerText = text.toLowerCase();

        // Case-insensitive match in verticalsFallbacks['fr']
        const foundVerticalFrKey = Object.keys(verticalsFallbacks['fr'] || {}).find(
          (k) => k.toLowerCase() === lowerText
        );
        if (foundVerticalFrKey && verticalsFallbacks['fr'][foundVerticalFrKey]) {
          return verticalsFallbacks['fr'][foundVerticalFrKey];
        }

        // Case-insensitive match in billingFallbacks['fr']
        const foundBillingFrKey = Object.keys(billingFallbacks['fr'] || {}).find(
          (k) => k.toLowerCase() === lowerText
        );
        if (foundBillingFrKey && billingFallbacks['fr'][foundBillingFrKey]) {
          return billingFallbacks['fr'][foundBillingFrKey];
        }

        // Case-insensitive match in hotelFallbacks['fr']
        const foundHotelFrKey = Object.keys(hotelFallbacks['fr'] || {}).find(
          (k) => k.toLowerCase() === lowerText
        );
        if (foundHotelFrKey && hotelFallbacks['fr'][foundHotelFrKey]) {
          return hotelFallbacks['fr'][foundHotelFrKey];
        }

        // Case-insensitive match in storefrontFallbacks['fr']
        const foundStorefrontFrKey = Object.keys(storefrontFallbacks['fr'] || {}).find(
          (k) => k.toLowerCase() === lowerText
        );
        if (foundStorefrontFrKey && storefrontFallbacks['fr'][foundStorefrontFrKey]) {
          return storefrontFallbacks['fr'][foundStorefrontFrKey];
        }

        // Case-insensitive match in mobileMoneyFallbacks['fr']
        const foundMomoFrKey = Object.keys(mobileMoneyFallbacks['fr'] || {}).find(
          (k) => k.toLowerCase() === lowerText
        );
        if (foundMomoFrKey && mobileMoneyFallbacks['fr'][foundMomoFrKey]) {
          return mobileMoneyFallbacks['fr'][foundMomoFrKey];
        }

        // Case-insensitive match in jewelryFallbacks['fr']
        const foundJewelryFrKey = Object.keys(jewelryFallbacks['fr'] || {}).find(
          (k) => k.toLowerCase() === lowerText
        );
        if (foundJewelryFrKey && jewelryFallbacks['fr'][foundJewelryFrKey]) {
          return jewelryFallbacks['fr'][foundJewelryFrKey];
        }

        // Case-insensitive match in pharmacyFallbacks['fr']
        const foundFrKey = Object.keys(pharmacyFallbacks['fr'] || {}).find(
          (k) => k.toLowerCase() === lowerText
        );
        if (foundFrKey && pharmacyFallbacks['fr'][foundFrKey]) {
          return pharmacyFallbacks['fr'][foundFrKey];
        }

        // Case-insensitive match in fallbacks['fr']
        const foundFallbackFrKey = Object.keys(fallbacks['fr'] || {}).find(
          (k) => k.toLowerCase() === lowerText
        );
        if (foundFallbackFrKey && fallbacks['fr'][foundFallbackFrKey]) {
          return fallbacks['fr'][foundFallbackFrKey];
        }

        const preferredLangData = translationMap.get(lowerText);
        if (preferredLangData) {
          const typedPreferredData = preferredLangData as any;
          const value = typedPreferredData['fr'] || typedPreferredData[currentLang?.key];
          if (value) return value;
        }

        return text;
      } else {
        // English is active: if text is in French, reverse-lookup to English!
        const lowerText = text.toLowerCase().trim();

        // Exact / Case-insensitive reverse match from verticalsFallbacks.fr -> English
        const foundVerticalEnKey = Object.keys(verticalsFallbacks['fr'] || {}).find(
          (enKey) => (verticalsFallbacks['fr'][enKey] || '').toLowerCase().trim() === lowerText
        );
        if (foundVerticalEnKey) return foundVerticalEnKey;

        // Exact / Case-insensitive reverse match from billingFallbacks.fr -> English
        const foundBillingEnKey = Object.keys(billingFallbacks['fr'] || {}).find(
          (enKey) => (billingFallbacks['fr'][enKey] || '').toLowerCase().trim() === lowerText
        );
        if (foundBillingEnKey) return foundBillingEnKey;

        // Exact / Case-insensitive reverse match from hotelFallbacks.fr -> English
        const foundHotelEnKey = Object.keys(hotelFallbacks['fr'] || {}).find(
          (enKey) => (hotelFallbacks['fr'][enKey] || '').toLowerCase().trim() === lowerText
        );
        if (foundHotelEnKey) return foundHotelEnKey;

        // Exact / Case-insensitive reverse match from fallbacks.fr -> English (prioritize non-identical English keys)
        const foundEnKey = Object.keys(fallbacks['fr'] || {}).find(
          (enKey) => enKey.toLowerCase().trim() !== lowerText && (fallbacks['fr'][enKey] || '').toLowerCase().trim() === lowerText
        );
        if (foundEnKey) return foundEnKey;

        // Exact / Case-insensitive reverse match from storefrontFallbacks.fr -> English (prioritize non-identical English keys)
        const foundStorefrontEnKey = Object.keys(storefrontFallbacks['fr'] || {}).find(
          (enKey) => enKey.toLowerCase().trim() !== lowerText && (storefrontFallbacks['fr'][enKey] || '').toLowerCase().trim() === lowerText
        );
        if (foundStorefrontEnKey) return foundStorefrontEnKey;

        // Exact / Case-insensitive reverse match from mobileMoneyFallbacks.fr -> English
        const foundMomoEnKey = Object.keys(mobileMoneyFallbacks['fr'] || {}).find(
          (enKey) => (mobileMoneyFallbacks['fr'][enKey] || '').toLowerCase().trim() === lowerText
        );
        if (foundMomoEnKey) return foundMomoEnKey;

        // Exact / Case-insensitive reverse match from pharmacyFallbacks.fr -> English
        const foundPharmEnKey = Object.keys(pharmacyFallbacks['fr'] || {}).find(
          (enKey) => (pharmacyFallbacks['fr'][enKey] || '').toLowerCase().trim() === lowerText
        );
        if (foundPharmEnKey) return foundPharmEnKey;

        const preferredLangData = translationMap.get(lowerText);
        if (preferredLangData) {
          const typedPreferredData = preferredLangData as any;
          const value = typedPreferredData['en'] || typedPreferredData[defaultLang];
          if (value) return value;
        }

        return text;
      }
    },
    [currentLanguage, translationMap, currentLang]
  );

  dayjs.extend(localizedFormat);
  dayjs.locale(currentLang?.key);

  const formatDate = (dateString: string) => {
    const datePart = dateString?.split(' - ')[0];
    return dayjs(datePart).format('LL');
  };

  useEffect(() => {
    if (refreshToken && successChangeLang && isManualChange) {
      Toast(api, 'Success', changeLangResponse);
      setIsManualChange(false);
    }
  }, [changeLangResponse, successChangeLang, errorChangeLang, isManualChange]);

  return (
    <LanguageContext.Provider
      value={{
        translate,
        menuProps,
        currentLang,
        isLoading,
        isLoadingLanguage,
        currentLanguage,
        formatDate,
        loadChangeLang,
      }}
    >
      {contectHolder}
      {children}
    </LanguageContext.Provider>
  );
};

const useLanguage = () => {
  const languageContext = useContext(LanguageContext);
  if (!languageContext) {
    throw new Error('Platform configuration not available');
  }
  return languageContext;
};

export { useLanguage, LanguageProvider };
